/* eslint-disable import/no-cycle */
import { compact, get, keyBy, reduce, uniq } from 'lodash';
import {
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
  onboardingService,
  financeService,
  centreSerivce,
  schoolService,
  userService,
  childService,
  transactionService,
} from 'utils/msEndpointConstants';
import moment from 'moment';
import DatadogHandler from '../utils/datadog';
import {
  DEFAULT_ERROR,
  SAVE_DATE_FORMAT,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE,
  APPLICANT,
  DATE_PICKER_FORMAT,
  MAX_PER_PAGE,
  UPLOAD_ERROR,
} from '../utils/constants';
import { AxiosHelper } from '../utils/ms-utils/axiosRequest';
import {
  retrieveListOrObject,
  retrieveSortForMS,
  retrieveObjectMapper,
  retrieveDataMapper,
  convertObjToSnakeCase,
  convertObjToCamelCase,
  convertPascalToSnake,
} from '../utils';
import {
  transformFetchExistingChild,
  transformRegistrationById,
  transformRegistrationChild,
  transformECDAExistingParent,
  transformECDARegistrations,
  transformECDAExistingChild,
  transformEnrolmentForm,
  transformMsFetchEnquiryDataByIdECDAMVP,
  transformGetRegistrations,
  transformGetECDARegUploadDocumentList,
  transformExistingParent,
  transformRegistrationInvoice,
  transformFormSigners,
} from '../utils/ms-utils/ms-phase-3/RegistrationUtils';
import {
  transformFetchAccountInfoByRegChild,
  transformFetchEgiroDetailsByECDARegChild,
} from '../utils/ms-utils/ms-phase-3/FinanceUtils';
import {
  msGetChildBankAccountDetails,
  getBankBicCodeByIds,
  getBankAccountStateHistoryByAccountIds,
} from './BankAccountService';
import { getFeeTierByRegId } from './FeeService';
import {
  getFormByRegistrationId,
  getFormParticipantsByFormIds,
} from './FormService';
import {
  getParentByIds,
  getUserByIds,
  getUserByAcknowledgeById,
  getStaffUserByStaffIds,
} from './UserService';
import { executePromiseMap } from '../utils/ms-utils/ms-phase-3/CommonUtils';
import {
  getDocumentByIds,
  getDocumentTagsByDocTypeIds,
  getDocumentTypeByFilter,
  getChildByIds,
  getDocumentTypesByBankAccountIds,
} from './ChildService';
import { getBillableItemsByIds, msGetInvoiceItemByIds } from './InvoiceService';

const fileName = 'src/services/RegistrationsService.js';

const {
  REGISTRATIONS,
  BANK_ACCOUNT,
  ENROLMENT_PLANNING,
  CENTRE: CENTRE_P3,
  CHILD: CHILD_P3,
  ECDA_REGISTRATIONS,
  CENTRE_WAITLIST,
  REFUND,
  USER: USER_P3,
  FINANCE,
  INVOICE,
  SCHOOL: SCHOOL_P3,
  TRANSACTION,
} = MS_PHASE_3_ENDPOINTS;
const { CHILD, CENTRE, SCHOOL, USER } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;

export const transformDiscountData = (resp, discountItems = []) => {
  const data = get(resp, 'data', {});
  return {
    totalCount: get(data, 'totalCount', 0),
    data: get(data, 'data', []).map(dt => {
      return {
        ...retrieveObjectMapper(dt, [
          'ID:id',
          'fkCentre:fk_centre',
          'fkChild:fk_child',
          'fkRegistrationChild:fk_registration_child',
          'fkDiscountItem:fk_discount_item',
          'fkEcdaRegistrationChild:fk_ecda_registration_child',
          'amount',
          'from',
          'to',
          'isRecurrent:is_recurrent',
          'active',
          'createdAt:created_at',
          'updatedAt:updated_at',
        ]),
        discountItem: retrieveDataMapper(
          discountItems,
          dt.fk_discount_item,
          [
            'name',
            'type',
            'discountItemType:discount_item_type_string',
            'startDate:start_date',
            'isRecurrent:is_recurrent',
            'ceasedDate:ceased_date',
            'amount',
          ],
          'id'
        ),
      };
    }),
  };
};

export const getRegistrationChildMappingByRegistrationChildIds = (
  restrationChildIds = []
) => {
  if (restrationChildIds.length === 0) return Promise.resolve({});
  return onboardingService()
    .post(
      ...REGISTRATIONS.getRegistrationChildMappingByRegistrationChildIds(
        restrationChildIds,
        { sort: '-created_at' }
      )
    )
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getRegChildMappingByRegistrationId = registrationId => {
  if (!registrationId) {
    return {};
  }
  return onboardingService()
    .get(REGISTRATIONS.getRegistrationChildMappingByRegId(registrationId))
    .then(r => r.data)
    .catch(() => ({}));
};

export const getRegistrationChildByIds = (registrationChildIds = []) => {
  if (registrationChildIds.length === 0) return Promise.resolve({});
  return onboardingService()
    .post(
      ...REGISTRATIONS.getRegistrationChildByIds(registrationChildIds, {
        sort: '-created_at',
      })
    )
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getRegistrationChildById = async regId => {
  if (!regId) {
    return Promise.resolve({});
  }
  return onboardingService()
    .get(REGISTRATIONS.getRegistrationChildById(regId))
    .then(r => r.data)
    .catch(() => ({}));
};

export const getCodeById = async codeId => {
  if (!codeId) return {};

  return schoolService()
    .get(SCHOOL.GET_CODE_BY_ID(codeId))
    .then(r => r.data)
    .catch(() => ({}));
};

export const msFetchRegistrationStates = async () => {
  try {
    const onboardingSvc = onboardingService();
    const result = await onboardingSvc.get(
      REGISTRATIONS.getRegFrontendStates()
    );
    return {
      success: true,
      data: {
        getRegistrationFrontendStates: result?.data?.states || [],
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchRegistrationStates',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchRegistrations = async (_, reqBody) => {
  try {
    const onboardingSvc = onboardingService();
    const centreSvc = centreSerivce();
    const schoolSvc = schoolService();
    const childSvc = childService();
    const levelIds = new Set();
    const centreIds = new Set();
    const programIds = new Set();
    const regChildIds = new Set();

    const resp = await onboardingSvc.post(
      ...REGISTRATIONS.getRegistrations(
        {
          centre_ids: get(reqBody, 'centres') || [],
          level_ids: get(reqBody, 'levels') || [],
          program_ids: get(reqBody, 'programs') || [],
          statuses: get(reqBody, 'statuses') || [],
          ...(get(reqBody, 'createdFrom') && {
            created_from: get(reqBody, 'createdFrom'),
          }),
          ...(get(reqBody, 'createdTo') && {
            created_to: get(reqBody, 'createdTo'),
          }),
          ...(get(reqBody, 'enrollmentDateFrom') && {
            enrollment_date_from: get(reqBody, 'enrollmentDateFrom'),
          }),
          ...(get(reqBody, 'enrollmentDateTo') && {
            enrollment_date_to: get(reqBody, 'enrollmentDateTo'),
          }),
          ...(get(reqBody, 'searchString') && {
            search_string: get(reqBody, 'searchString'),
          }),
        },
        {
          page: get(reqBody, 'pagination.page', DEFAULT_PAGE),
          perPage: get(reqBody, 'pagination.perPage', DEFAULT_ITEMS_PER_PAGE),
          ...retrieveSortForMS(get(reqBody, 'pagination.sort', []), 'sort'),
        }
      )
    );

    const regIds = get(resp, 'data.data', []).map(r => get(r, 'id'));
    const regChildMappings = await onboardingSvc
      .post(...REGISTRATIONS.getRegChildMappingByRegIds(regIds))
      .then(r => retrieveListOrObject(r, false))
      .catch(() => ({}));

    const flattedReqChildMappings = Object.values(
      regChildMappings || {}
    ).reduce((acc, curr) => [...acc, ...get(curr, 'data', [])], []);

    flattedReqChildMappings.forEach(d => {
      levelIds.add(get(d, 'fk_level'));
      centreIds.add(get(d, 'fk_centre'));
      programIds.add(get(d, 'fk_program'));
      regChildIds.add(get(d, 'fk_registration_child'));
    });

    const [reqChildren, centres, programs, levels, applicants, regParents] =
      await Promise.all([
        regChildIds?.size > 0
          ? onboardingSvc
              .post(
                ...REGISTRATIONS.getRegistrationChildByIds(
                  compact([...regChildIds])
                )
              )
              .then(r => retrieveListOrObject(r, false))
              .catch(() => ({}))
          : {},
        centreIds?.size > 0
          ? centreSvc
              .post(...CENTRE.GET_CENTRE_BY_IDS(compact([...centreIds])))
              .then(retrieveListOrObject)
              .catch(() => [])
          : {},
        programIds?.size > 0
          ? centreSvc
              .post(...CENTRE.GET_PROGRAMS_BY_IDS(compact([...programIds])))
              .then(retrieveListOrObject)
              .catch(() => [])
          : {},
        levelIds?.size > 0
          ? schoolSvc
              .post(...SCHOOL.GET_LEVEL_BY_IDS(compact([...levelIds])))
              .then(retrieveListOrObject)
              .catch(() => [])
          : {},
        regChildIds?.size > 0
          ? childSvc
              .get(
                ...CHILD_P3.getApplicantByChildIds(compact([...regChildIds]))
              )
              .then(retrieveListOrObject)
              .catch(() => [])
          : {},
        regChildIds?.size > 0
          ? onboardingSvc
              .get(
                ...REGISTRATIONS.getRegParentsByRegChildIds(
                  compact([...regChildIds])
                )
              )
              .then(r => retrieveListOrObject(r, false))
              .catch(() => ({}))
          : {},
      ]);
    return transformGetRegistrations(
      resp,
      regChildMappings,
      reqChildren,
      centres,
      programs,
      levels,
      applicants,
      regParents
    );
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchRegistrations',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetRegEnrolmentForm = async (_, { registrationID }) => {
  try {
    const [regEnrolment, regChildMapping] = await Promise.all([
      onboardingService().get(
        REGISTRATIONS.getRegEnrolmentForm(registrationID)
      ),
      getRegChildMappingByRegistrationId(registrationID),
    ]);
    const regEnrolmentForm = convertObjToCamelCase(
      regEnrolment.data,
      [],
      [
        'ID:id',
        'type:type_string',
        'documentStatus:document_status_string',
        'consentStatus:consent_status_string',
        'giroStatus:giro_status_string',
        'paymentStatus:payment_status_string',
      ]
    );
    const registrationChildMapping = {
      data: [convertObjToCamelCase(regChildMapping)],
    };

    return {
      data: {
        getRegEnrolmentForm: {
          ...regEnrolmentForm,
          registration: {
            ...regEnrolmentForm.registration,
            registrationChildMappings: registrationChildMapping,
          },
          child: [
            {
              ...get(regEnrolmentForm, 'child.[0]', {}),
              registrationChildMappings: registrationChildMapping,
            },
          ],
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetRegEnrolmentForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchEnquiryDataById = async (_, reqData) => {
  try {
    const id = get(reqData, 'registrationID');
    const [resp, feeTier, form] = await Promise.all([
      onboardingService().get(REGISTRATIONS.getRegistrationByID(id)),
      getFeeTierByRegId(id),
      getFormByRegistrationId(id),
    ]);
    const userId = get(resp, 'data.fk_user');
    const fkCancelCode = get(resp, 'data.fk_cancel_code') || 1;
    const formIds = get(form, 'data', [])?.map(f => get(f, 'id'));
    const [regChildMappings, staffUsers, code, formParticipants] =
      await Promise.all([
        onboardingService()
          .post(
            ...REGISTRATIONS.getAllReqChildMappings({
              id: [id],
            })
          )
          .then(r => get(r, 'data.data', []))
          .catch(() => []),
        userService()
          .post(...USER.GET_STAFF_USER_BY_USER_IDS([userId]))
          .then(retrieveListOrObject)
          .catch(() => ({})),
        fkCancelCode
          ? schoolService()
              .get(SCHOOL.GET_CODE_BY_ID(fkCancelCode))
              .then(r => r.data)
              .catch(() => ({}))
          : null,
        getFormParticipantsByFormIds(formIds),
      ]);

    const participantsFlatted = Object.values(formParticipants || {}).reduce(
      (acc, curr) => [...acc, ...get(curr, 'data', [])],
      []
    );

    const staffUser = get(
      Object.entries(staffUsers).find(
        ([, obj]) => get(obj, 'data[0].user.id') === userId
      ),
      '[1]?.data?.[0]',
      {}
    );
    const regChildId = get(regChildMappings, '[0].fk_registration_child');
    const centreIds = new Set();
    const programIds = new Set();
    const levelIds = new Set();
    const userIds = compact(uniq(participantsFlatted?.map(p => p?.fk_user)));
    const parentIds = compact(
      uniq(
        participantsFlatted?.map(p => p?.fk_parent || p?.fk_registration_parent)
      )
    );

    (regChildMappings || []).forEach(f => {
      if (f.fk_centre) {
        centreIds.add(f.fk_centre);
      }
      if (f.fk_program) {
        programIds.add(f.fk_program);
      }
      if (f.fk_level) {
        levelIds.add(f.fk_level);
      }
    });

    const [centreByIds, addresses, programByIds, levelByIds, users, parents] =
      await Promise.all([
        centreSerivce()
          .post(...CENTRE.GET_CENTRE_BY_IDS([...centreIds]))
          .then(retrieveListOrObject)
          .catch(() => []),
        Promise.all(
          [...centreIds].map(centreId =>
            userService()
              .get(USER.GET_ADDRESS_BY_CENTRE_ID(centreId))
              .then(r => r.data)
              .catch(() => ({}))
          )
        ),
        centreSerivce()
          .post(...CENTRE.GET_PROGRAMS_BY_IDS([...programIds]))
          .then(retrieveListOrObject)
          .catch(() => []),
        schoolService()
          .post(...SCHOOL.GET_LEVEL_BY_IDS([...levelIds]))
          .then(retrieveListOrObject)
          .catch(() => []),
        getUserByIds(userIds),
        getParentByIds(parentIds),
      ]);

    const regChild = await getRegistrationChildById(regChildId);

    const [regChildParents, mainApplicantAndSiblings] = await Promise.all([
      onboardingService()
        .get(REGISTRATIONS.getRegistrationChildParents(regChild.id))
        .then(res =>
          retrieveListOrObject(res).filter(
            item => get(item, 'type_string') === 'applicant'
          )
        )
        .catch(() => []),
      childService()
        .get(CHILD.GET_APPLICANT_OF_CHILD(regChild.id))
        .then(r =>
          Promise.all([
            userService()
              .get(USER.GET_PARENT_BY_ID(get(r, 'data.parent_id')))
              .catch(() => ({})),
            childService()
              .get(
                CHILD.GET_SIBLINGS_BY_CHILDBC_AND_PARENT_ID(
                  get(regChild, 'birth_certificate'),
                  get(r, 'data.parent_id')
                )
              )
              .catch(() => ({})),
          ])
        )
        .then(([mainApplicant, siblings]) => ({
          mainApplicant: get(mainApplicant, 'data', {}),
          siblings: get(siblings, 'data.data', []),
        }))
        .catch(() => ({})),
    ]);

    const reqParentIds = [
      ...new Set(
        regChildParents.map(item => get(item, 'fk_registration_parent'))
      ),
    ].filter(Boolean);

    const [regParents, addressByRegParents] = await Promise.all([
      onboardingService()
        .post(...REGISTRATIONS.getReqParentsByIds(reqParentIds))
        .then(res => retrieveListOrObject(res, false))
        .catch(() => ({})),
      userService()
        .post(...USER.getAddressByReqParentIds(reqParentIds))
        .then(retrieveListOrObject)
        .catch(() => []),
    ]);

    return transformRegistrationById(
      ...[
        resp,
        feeTier,
        form,
        centreByIds,
        addresses,
        programByIds,
        levelByIds,
        formParticipants,
        users,
        parents,
        regChild,
        regChildParents,
        mainApplicantAndSiblings,
        regParents,
        addressByRegParents,
        staffUser,
        regChildMappings,
        code,
      ]
    );
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchEnquiryDataById',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAccountInfoByChild = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const result = await financeSvc.post(
      ...BANK_ACCOUNT.getBankAccountByChild(reqData)
    );

    const bankAccounts = result?.data?.data ?? [];
    const data = await msGetChildBankAccountDetails(bankAccounts);
    return {
      success: true,
      data: {
        getBankAccountByFkRegistrationChild: data,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msfetchAccountInfoByChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      data: { getBankAccountByFkRegistrationChild: [] },
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetMonthlyFeeForEnquiry = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const res = await financeService().get(
      FINANCE.getRegistrationDraftMonthlyFeeInvoice(IDRegistration)
    );

    const data = res?.data || {};
    return {
      data: {
        getRegistrationDraftMonthlyFeeInvoice: {
          totalAmount: data.total_amount,
          outstandingAmount: data.outstanding_amount,
          status: data.status_string,
          grandTotal: data.grand_total,
          invoiceItems: {
            data: [
              {
                label: data.label,
                totalAmount: data.grand_total,
                billableItem: {
                  type: 'fee',
                },
              },
            ],
          },
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetMonthlyFeeForEnquiry',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetPaymentDetailsForEnquiry = async (_, reqData) => {
  try {
    const regId = get(reqData, 'registrationID');
    const resp = await financeService().get(
      `${REGISTRATIONS.getInvoiceByRegId}?registrationID=${regId}`
    );
    const invoiceId = get(resp, 'data.id');
    const [invoiceItems, receipts] = await Promise.all([
      financeService()
        .post(
          ...INVOICE.getInvoiceItemsByInvoiceIds({
            invoice_ids: [invoiceId],
          })
        )
        .catch(() => ({})),
      financeService()
        .post(...FINANCE.getReceiptByInvoiceIds([invoiceId]))
        .then(r => get(r, 'data.result', {}))
        .catch(() => ({})),
    ]);

    const billableItemIds = uniq(
      compact(
        get(invoiceItems, 'data.data', []).map(item =>
          get(item, 'fk_billable_item')
        )
      )
    );

    const fkStaffs = uniq(
      compact(
        (get(receipts, invoiceId) || [])?.map(r =>
          get(r, 'collected_by_fk_staff')
        )
      )
    );

    const [billableItems, staffUsers] = await Promise.all([
      getBillableItemsByIds(billableItemIds),
      getStaffUserByStaffIds(fkStaffs),
    ]);

    return transformRegistrationInvoice(
      resp,
      invoiceId,
      invoiceItems,
      billableItems,
      receipts,
      staffUsers
    );
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetPaymentDetailsForEnquiry',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchEgiroDetailsByRegChild = async (_, reqData) => {
  try {
    const registrationChildId = get(reqData, 'registrationChildId');
    const resp = await financeService().get(
      ...FINANCE.getBankAccountByFkRegistrationChild(registrationChildId)
    );

    const bankAccounts = get(resp, 'data.bank_accounts', []);
    const childIds = compact(
      uniq(bankAccounts.map(account => get(account, 'fk_child')))
    );

    const bankBicCodeIds = compact(
      uniq(bankAccounts.map(account => get(account, 'fk_bank_bic_code')))
    );

    const bankAccountIds = compact(
      uniq(bankAccounts.map(account => get(account, 'id')))
    );

    const batchResponses = await Promise.all([
      getChildByIds(childIds),
      getBankBicCodeByIds(bankBicCodeIds),
      getBankAccountStateHistoryByAccountIds(bankAccountIds),
      getDocumentTypesByBankAccountIds(bankAccountIds),
    ]);

    return transformFetchAccountInfoByRegChild(...[resp, ...batchResponses]);
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchEgiroDetailsByRegChild',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllChildDiscounts = async (_, reqBody) => {
  try {
    const childID = get(reqBody, 'childID');
    const response = await financeService().post(
      ...FINANCE.getAllChildDiscounts({ childID })
    );
    const discountItems = await financeService().post(
      ...FINANCE.getDiscountItemsByIds(
        get(response, 'data.data', []).map(item =>
          get(item, 'fk_discount_item')
        )
      )
    );

    const transformedResponse = transformDiscountData(
      response,
      get(discountItems, 'data.data', [])
    );

    return {
      data: {
        getAllChildDiscounts: transformedResponse,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllChildDiscounts',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllChildDiscountsRegistration = async (_, reqBody) => {
  try {
    const registrationChildID = get(reqBody, 'registrationChildID');
    const filter = get(reqBody, 'filter', {});
    const response = await financeService().post(
      ...FINANCE.getAllRegistrationChildDiscounts({
        childID: registrationChildID,
        filter,
      })
    );

    const discountItemsRes = await financeService().post(
      ...FINANCE.getDiscountItemsByIds(
        get(response, 'data.data', []).map(item =>
          get(item, 'fk_discount_item')
        )
      )
    );

    const transformedResponse = transformDiscountData(
      response,
      get(discountItemsRes, 'data.data', [])
    );

    return {
      data: {
        getAllRegistrationChildDiscounts: transformedResponse,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllChildDiscountsRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddDiscountToRegistration = async (_, reqData) => {
  try {
    const registrationChildID = get(reqData, 'registrationChildID');
    const registrationID = get(reqData, 'registrationID');
    const discountItemGroupID = get(reqData, 'discountItemGroupID');
    const from = moment(get(reqData, 'from')).format('YYYY-MM-DD HH:mm:ss');
    const to = moment(get(reqData, 'to')).format('YYYY-MM-DD HH:mm:ss');
    const amount = get(reqData, 'amount');
    const remarks = get(reqData, 'remarks');

    const response = await financeService().post(
      ...FINANCE.addRegistrationChildDiscount({
        registration_child_id: registrationChildID,
        registration_id: registrationID,
        discount_item_group_id: discountItemGroupID,
        from,
        to,
        amount,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        addDiscountToRegistrationChild: {
          ID: get(responseData, 'id'),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddDiscountToRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSaveRegEnrolmentForm = async (_, reqData) => {
  try {
    const { registrationID, regForm, step } = reqData;
    const form = convertObjToSnakeCase(
      regForm,
      [],
      [
        'registrationChild:registrationChild',
        'registrationChildId:registrationChildId',
        'mainApplicant:mainApplicant',
        'registrationChildParent:registrationChildParent',
        'registrationParent:registrationParent',
        'registrationParentGuardian:registrationParentGuardian',
        'registrationParentId:registrationParentId',
        'familyMembers:familyMembers',
      ]
    );
    const [resp, regChildMapping] = await Promise.all([
      onboardingService().post(REGISTRATIONS.saveRegEnrolmentForm, {
        registration_id: registrationID,
        step,
        registration_form: form,
      }),
      getRegChildMappingByRegistrationId(registrationID),
    ]);

    const regEnrolmentForm = convertObjToCamelCase(
      resp.data,
      [],
      [
        'ID:id',
        'type:type_string',
        'documentStatus:document_status_string',
        'consentStatus:consent_status_string',
        'giroStatus:giro_status_string',
        'paymentStatus:payment_status_string',
      ]
    );
    const registrationChildMapping = {
      data: [convertObjToCamelCase(regChildMapping)],
    };

    return {
      data: {
        saveRegEnrolmentForm: {
          ...regEnrolmentForm,
          registration: {
            ...regEnrolmentForm.registration,
            registrationChildMappings: registrationChildMapping,
          },
          child: [
            {
              ...get(regEnrolmentForm, 'child.[0]', {}),
              registrationChildMappings: registrationChildMapping,
            },
          ],
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveRegEnrolmentForm',
        fileName,
      },
      'error'
    );

    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msFetchRegAdditionalInfo = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            getRegAdditionalInfo: {},
          },
          success: true,
        });
      }, 1000);
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchRegAdditionalInfo',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSaveRegAdditionalInfo = async (_, reqData) => {
  const { registrationID, regForm, step } = reqData;
  const form = regForm;
  if (regForm.EmergencyContactInfo) {
    form.EmergencyContactInfo = convertObjToSnakeCase(
      regForm.EmergencyContactInfo
    );
  }
  if (regForm.Registration) {
    form.Registration = convertObjToSnakeCase(regForm.Registration);
  }
  if (regForm.MarketingChannels) {
    form.MarketingChannels = convertObjToSnakeCase(regForm.MarketingChannels);
  }
  try {
    const resp = await onboardingService().post(
      REGISTRATIONS.saveRegAdditionalInfo,
      {
        registration_id: registrationID,
        step,
        registration_form: form,
      }
    );
    const data = resp?.data || {};

    return {
      data: {
        saveRegAdditionalInfo: {
          HouseholdInfo: {
            PrimaryApplicantHighestEducation:
              data.household_info?.primary_applicant_highest_education,
            SecondaryApplicantHighestEducation:
              data.household_info?.secondary_applicant_highest_education,
            FamilyMemberCount: data.household_info?.family_member_count,
          },
          EmergencyContactInfo: convertObjToCamelCase(
            data.emergency_contact_info,
            ['mobilePhoneCC:mobile_phone_cc']
          ),
          HealthInfo: data.health_info,
          MarketingChannels: {
            IsEmail: data.marketing_channels?.is_email,
            IsSMS: data.marketing_channels?.is_sms,
            IsPhone: data.marketing_channels?.is_phone,
          },
          Registration: convertObjToCamelCase(data.registration),
          Step: data.step,
          IsConsented: data.is_consented,
          MarketingConsent: data.marketing_consent,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveRegAdditionalInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetUploadDocumentList = async (_, { IDRegistration }) => {
  try {
    const childSvc = childService();
    const onboardingSvc = onboardingService();
    const [res, documentTypes] = await Promise.all([
      childSvc.get(CHILD_P3.getRequiredRegDocuments(IDRegistration)),
      getDocumentTypeByFilter({
        fk_registrations: [Number(IDRegistration)],
        pagination: {
          page: 1,
          perPage: MAX_PER_PAGE,
        },
      }),
    ]);
    const documents = get(res, 'data.data', []);
    const documentTypeConfigIds = compact(
      uniq(documents.map(item => item?.fk_document_type_config))
    );
    const regChildId = get(documents, '0.fk_registration_child');
    const documentTypeIds = documentTypes.map(item => item.id);

    const [
      regChildren,
      regParentsByChildId,
      documentTypeConfigs,
      familyMembers,
      documentTags,
    ] = await Promise.all([
      getRegistrationChildByIds([regChildId]),
      onboardingSvc
        .get(...REGISTRATIONS.getRegParentsByRegChildIds([regChildId]))
        .then(r => convertObjToCamelCase(retrieveListOrObject(r, false)))
        .catch(() => ({})),
      childSvc
        .get(...CHILD_P3.getDocumentTypeConfigs(documentTypeConfigIds))
        .then(dt => convertObjToCamelCase(retrieveListOrObject(dt, false)))
        .catch(() => ({})),
      onboardingSvc
        .get(REGISTRATIONS.getFamilyMembersByRegChildId(regChildId))
        .then(r => retrieveListOrObject(r, true))
        .catch(() => ({})),
      getDocumentTagsByDocTypeIds(documentTypeIds),
    ]);

    const fkDocuments = Object.values(documentTags).flatMap(item =>
      item.data ? item.data.map(d => d.fk_document) : []
    );
    const uploadedDocuments = await getDocumentByIds(fkDocuments);
    const regParents = regParentsByChildId?.[regChildId]?.data || [];
    const regChild = convertObjToCamelCase(regChildren);
    const documentTypesByTypeConfig = keyBy(
      documentTypes,
      'fk_document_type_config'
    );
    const uploadedByUserIds = Object.values(uploadedDocuments).flatMap(
      item => item.uploaded_by
    );
    const users = await getUserByIds(uploadedByUserIds);
    const userMap = keyBy(convertObjToCamelCase(users), 'ID');

    return {
      data: {
        getRequiredRegDocuments: {
          data: documents.map(doc => {
            return {
              ...convertObjToCamelCase(doc, ['status:status_string']),
              documentTypeConfig:
                documentTypeConfigs[doc?.fk_document_type_config],
              registrationChild: regChild[doc?.fk_registration_child],
              registrationParent: doc?.fk_registration_parent
                ? regParents.find(
                    parent => parent?.ID === doc?.fk_registration_parent
                  )
                : null,
              registrationFamilyMember: convertObjToCamelCase(
                familyMembers.find(
                  member => member?.ID === doc?.fk_family_member
                )
              ),
              documentTags: {
                data: (
                  documentTags[
                    documentTypesByTypeConfig[doc?.fk_document_type_config]?.id
                  ]?.data || []
                ).map(tag => ({
                  ID: tag?.id,
                  document: {
                    ...convertObjToCamelCase(
                      uploadedDocuments[tag?.fk_document],
                      ['uRL:url', 'preSignedUrl:url_presigned_url']
                    ),
                    user: uploadedDocuments[tag?.fk_document]?.uploaded_by
                      ? userMap[
                          uploadedDocuments[tag?.fk_document]?.uploaded_by
                        ]
                      : null,
                  },
                })),
              },
            };
          }),
          totalCount: get(res, 'data.totalCount', 0),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetUploadDocumentList',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetDocumentTypeToUpload = async (_, reqData) => {
  try {
    const childSvc = childService();
    const endpoint = MS_PHASE_3_ENDPOINTS.CHILD.getDocumentTypeConfigsByFk();
    const payload = {
      id_document_type_config: get(reqData, 'IDDocumentTypeConfig'),
    };
    const resp = await childSvc
      .post(endpoint, payload)
      .then(r => get(r, 'data', { totalCount: 0, data: [] }))
      .catch(() => ({ totalCount: 0, data: [] }));

    const totalCount = get(resp, 'totalCount', 0);
    const data = get(resp, 'data', []).map(d =>
      retrieveObjectMapper(d, [
        'ID:id',
        'fk_school',
        'fk_document_type_config',
        'code',
        'label',
        'description',
        'upload_access',
        'list_access',
        'is_restricted',
        'active',
        'created_at',
        'updated_at',
      ])
    );

    return {
      data: {
        documentTypeConfigsByFkDocumentTypeConfig: {
          data,
          totalCount,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetDocumentTypeToUpload',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetRegistrationConfig = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {},
          success: true,
        });
      }, 1000);
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetRegistrationConfig',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSubmitRegForm = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const res = await onboardingService().post(
      REGISTRATIONS.submitRegistrationForm(IDRegistration)
    );

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSubmitRegForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msApproveRegDocument = async (
  _,
  { IDRegistration, IDDocumentType, remarks }
) => {
  try {
    const res = await childService().post(CHILD_P3.approveRegDocument, {
      registration_id: Number(IDRegistration),
      document_type_id: Number(IDDocumentType),
      remarks,
    });

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msApproveRegDocument',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [{ message: error?.response?.data?.error || DEFAULT_ERROR }],
    };
  }
};

export const msRevertRegDocument = async (
  _,
  { IDRegistration, IDDocumentType, remarks }
) => {
  try {
    const res = await childService().post(CHILD_P3.revertRegDocument, {
      registration_id: Number(IDRegistration),
      document_type_id: Number(IDDocumentType),
      remarks,
    });

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRevertRegDocument',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [{ message: error?.response?.data?.error || DEFAULT_ERROR }],
    };
  }
};

export const msRejectRegDocument = async (
  _,
  { IDRegistration, IDDocumentType, remarks }
) => {
  try {
    const res = await childService().post(CHILD_P3.rejectRegDocument, {
      registration_id: Number(IDRegistration),
      document_type_id: Number(IDDocumentType),
      remarks,
    });

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRejectRegDocument',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [{ message: error?.response?.data?.error || DEFAULT_ERROR }],
    };
  }
};

export const msRemoveRegUploadedDocument = async (_, regData) => {
  const { IDRegistration, IDDocument, remarks } = regData;
  try {
    const res = await childService().post(CHILD_P3.removeRegDocument, {
      registration_id: Number(IDRegistration),
      document_type_id: Number(IDDocument),
      remarks,
    });

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveRegUploadedDocument',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetRegistrationDocumentUploadURL = async (
  _,
  { IDRegistration, file }
) => {
  try {
    const res = await onboardingService().post(
      REGISTRATIONS.getRegistrationDocumentUploadUrl,
      {
        registration_id: Number(IDRegistration),
        file_upload: {
          file_name: file?.fileName,
          file_path: file?.filePath,
          file_size: file?.fileSize,
          mime_type: file?.mimeType,
        },
      }
    );

    return {
      data: {
        getRegistrationDocumentUploadUrl: {
          Url: res.data?.url,
          Key: res.data?.key,
          Filename: res.data?.filename,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetRegistrationDocumentUploadURL',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSaveUploadedRegDocument = async (
  _,
  { IDRegistration, uploads, skipApproval }
) => {
  try {
    const res = await childService().post(
      CHILD_P3.saveRegistrationGiroDocument,
      {
        registration_id: Number(IDRegistration),
        skip_approval: skipApproval,
        document_uploads: uploads.map(type => ({
          document_type_id: Number(type.documentTypeId),
          document_uploads: type.documentUploads.map(file => ({
            file_name: file?.filename,
            file_path: file?.filepath,
            file_size: file?.filesize,
            mime_type: file?.mimetype,
          })),
        })),
      }
    );

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveUploadedRegDocument',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSkipRegDocument = async (
  _,
  { IDRegistration, IDDocumentType, remarks }
) => {
  try {
    const res = await childService().post(CHILD_P3.skipRegDocument, {
      registration_id: Number(IDRegistration),
      document_type_id: Number(IDDocumentType),
      remarks,
    });

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSkipRegDocument',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [{ message: error?.response?.data?.error || DEFAULT_ERROR }],
    };
  }
};

export const msSendDocumentNotificationToParent = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: { sendDocumentStatusNotification: true },
          success: true,
        });
      }, 1000);
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSendDocumentNotificationToParent',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetFormSigner = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      REGISTRATIONS.getFormSigners(IDRegistration)
    );

    const formSigners = transformFormSigners(get(resp, 'data.data', []) || []);

    return {
      data: {
        getFormSigner: formSigners,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetFormSigner',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllOfflineConsentFormAction = async (
  _,
  { registrationId }
) => {
  try {
    const res = await childService().get(
      CHILD_P3.getOfflineConsentFormByRegistration(registrationId)
    );
    const data = res?.data;
    return {
      data: {
        getOfflineConsentFormByRegistration: {
          ID: data?.id,
          filename: data?.filename,
          label: data?.label,
          uRL: data?.url,
          preSignedUrl: data?.url_presigned_url,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllOfflineConsentFormAction',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSendConsentForm = async (_, reqData) => {
  try {
    const registrationConsentId =
      get(reqData, 'registrationConsentId') || get(reqData, 'IDRegistration');
    const formSigners =
      get(reqData, 'formSigners') ||
      get(reqData, 'formParticipants') ||
      get(reqData, 'participants', []);

    const onboardingSvc = onboardingService();
    const payload = {
      form_signers: formSigners.map(signer => ({
        ...convertPascalToSnake(signer, { UserID: signer?.UserId }),
      })),
    };

    const response = await onboardingSvc.post(
      REGISTRATIONS.sendConsentForm(registrationConsentId),
      payload
    );

    return {
      data: convertObjToCamelCase(get(response, 'data', {})),
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSendConsentForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCancelConsentForm = async (_, reqData) => {
  try {
    if (!get(reqData, 'IDRegistration')) {
      return {
        success: false,
        error: [{ message: DEFAULT_ERROR }],
      };
    }
    const resp = await onboardingService().post(
      REGISTRATIONS.cancelForm(get(reqData, 'IDRegistration'))
    );
    return {
      success: !!get(resp, 'data.id'),
      data: {
        cancelForm: convertObjToCamelCase(get(resp, 'data', {})),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelConsentForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msCompleteConsentForm = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    if (!IDRegistration) {
      return {
        success: false,
        error: [{ message: DEFAULT_ERROR }],
      };
    }
    const resp = await onboardingService().post(
      REGISTRATIONS.markConsentCompleted(IDRegistration)
    );
    return {
      success: !!get(resp, 'data.completed'),
      data: convertObjToCamelCase(get(resp, 'data', {})),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCompleteConsentForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msExportGiroPdf = async (_, reqData) => {
  try {
    const { bankAccountID, IDBankAccount, centreId } = reqData;
    const bankAccountId = bankAccountID || IDBankAccount;
    const requestBody = {
      bank_account_id: bankAccountId,
      centre_id: centreId,
    };

    const resp = await financeService().post(
      FINANCE.exportGiroApplicationPdf,
      requestBody
    );

    return {
      data: {
        exportGiroApplicationPdf: get(resp, 'data.pre_signed_url', ''),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msExportGiroPdf',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msEmailGiroPdf = async (_, reqData = {}) => {
  try {
    const {
      IDBankAccount: bankAccountId,
      centreId,
      childId,
      IDRegChild: regChildId,
      IDEcdaRegChild: ecdaRegChildId,
    } = reqData;

    const requestBody = {
      bank_account_id: bankAccountId,
      centre_id: centreId,
    };

    if (childId !== undefined && childId !== null) {
      requestBody.child_id = childId;
    }

    if (regChildId !== undefined && regChildId !== null) {
      requestBody.registration_child_id = regChildId;
    }

    if (ecdaRegChildId !== undefined && ecdaRegChildId !== null) {
      requestBody.ecda_registration_child_id = ecdaRegChildId;
    }

    const resp = await financeService().post(
      FINANCE.emailGiroApplicationPdf,
      requestBody
    );

    return {
      success: true,
      data: resp,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msEmailGiroPdf',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msCompleteGiroSetup = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: { markGiroAsCompleted: true },
          success: true,
        });
      }, 1000);
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCompleteGiroSetup',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchExistingParent = async (_, requestData) => {
  try {
    const resp = await onboardingService().post(
      REGISTRATIONS.getExistingParent,
      { email: get(requestData, 'email') }
    );
    return transformExistingParent(resp);
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchExistingParent',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetEnrolmentDateOptions = async (_, reqBody) => {
  try {
    const reqData = {
      ...(reqBody?.from && {
        from: reqBody?.from,
      }),
      ...(reqBody?.to && {
        to: reqBody?.to,
      }),
      centre_id: reqBody?.centreID,
      ...(reqBody?.programID && {
        program_id: reqBody?.programID,
      }),
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...ENROLMENT_PLANNING.getEnrolmentDates(reqData)
    );
    const getEnrolmentDates =
      resp?.data?.dates?.map(date => moment(date).format(SAVE_DATE_FORMAT)) ||
      [];
    return { getEnrolmentDates };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetEnrolmentDateOptions',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(error, 'message', DEFAULT_ERROR)],
    };
  }
};

export const msFetchExistingChild = async (_, reqData) => {
  try {
    const birthCertificate = get(reqData, 'birthCertificate');
    const dateOfBirth = get(reqData, 'dateOfBirth');
    const onboardingSvc = AxiosHelper({
      baseURL: process.env.REACT_APP_MS_ONBOARDING_SVC_URL,
    });
    const childSvc = AxiosHelper({
      baseURL: process.env.REACT_APP_MS_CHILD_SVC_URL,
    });
    const resp = await onboardingSvc.post(REGISTRATIONS.getExistingChild, {
      birth_certificate: birthCertificate,
      date_of_birth: dateOfBirth,
    });

    const respChild = get(resp, 'data.child');
    const respRegChild = get(resp, 'data.reg_child');
    let childRelations = [];
    let regChildParents = [];

    if (respChild?.id) {
      const [childRelationsResp, regChildParentsResp] = await Promise.all([
        childSvc
          .get(CHILD.GET_CHILD_RELATIONS_BY_ID(respChild.id))
          .then(rps =>
            retrieveListOrObject(rps).filter(
              item => get(item, 'type_string') === 'applicant'
            )
          )
          .catch(() => []),
        respRegChild
          ? onboardingSvc
              .get(REGISTRATIONS.getRegistrationChildParents(respRegChild.id))
              .then(res =>
                retrieveListOrObject(res).filter(
                  item => get(item, 'type_string') === 'applicant'
                )
              )
              .catch(() => [])
          : Promise.resolve([]),
      ]);
      childRelations = childRelationsResp;
      regChildParents = regChildParentsResp;
    }
    return transformFetchExistingChild(resp, childRelations, regChildParents);
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchExistingChild',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [{ message: get(error, 'message', DEFAULT_ERROR) }],
    };
  }
};

export const msEditRegistration = async (
  _,
  { enquiryID, editRegistration, remarks }
) => {
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.put(REGISTRATIONS.editRegistration, {
      registration_dto: {
        ...convertObjToSnakeCase(editRegistration, [
          'relationship_id:relationshipID',
        ]),
        enquiry_date: moment(editRegistration.enquiryDate).format(
          DATE_PICKER_FORMAT
        ),
        enrolment_date: moment(editRegistration.enrolmentDate).format(
          DATE_PICKER_FORMAT
        ),
        child_date_of_birth: moment(editRegistration.childDateOfBirth).format(
          DATE_PICKER_FORMAT
        ),
      },
      registration_id: enquiryID,
      remarks,
    });
    const regChildRes = await onboardingSvc.get(
      REGISTRATIONS.getRegChildByRegistrationId(enquiryID)
    );
    return {
      data: {
        editRegistration: {
          ID: get(resp, 'data.id', undefined),
          registrationChildMappings: {
            data: [
              {
                fkRegistrationChild: get(regChildRes, 'data.id', undefined),
              },
            ],
          },
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msEditRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'error', DEFAULT_ERROR),
    };
  }
};

export const msCreateRegistration = async (_, payload) => {
  try {
    const data = payload?.newRegistration || {};
    const res = await onboardingService().post(
      REGISTRATIONS.createRegistration,
      {
        ...convertObjToSnakeCase(data),
        child_date_of_birth: moment(data?.childDateOfBirth).format(
          DATE_PICKER_FORMAT
        ),
        enquiry_date: moment(data?.enquiryDate).format(DATE_PICKER_FORMAT),
        enrolment_date: moment(data?.enrolmentDate).format(DATE_PICKER_FORMAT),
      }
    );
    return {
      data: {
        createRegistration: {
          ID: get(res, 'data.id', undefined),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCreateRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCancelRegistration = async (_, reqData) => {
  try {
    const {
      enquiryID,
      isReapplication,
      remarks,
      cancellationReason,
      bypassExceeding,
    } = reqData;
    const res = await onboardingService().put(
      REGISTRATIONS.cancelRegistration,
      {
        registration_id: enquiryID,
        bypass_exceeding: bypassExceeding,
        is_reapplication: isReapplication,
        remarks,
        cancellation_reason_id: Number(cancellationReason),
      }
    );
    return {
      data: {
        cancelRegistration: {
          ID: get(res, 'data.id', undefined),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelRegistration',
        fileName,
      },
      'error'
    );

    return {
      data: undefined,
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msReinstateCancelledRegistration = async (_, regData) => {
  try {
    const { registrationID, remarks } = regData;
    const res = await onboardingService().put(
      REGISTRATIONS.reinstateCancelledRegistration,
      {
        registration_id: registrationID,
        remarks,
      }
    );
    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msReinstateCancelledRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msHasDuplicateSpouseEmailOrNric = async (_, reqData = {}) => {
  try {
    const resp = await onboardingService().post(
      ...REGISTRATIONS.requireConfirmationForSpouse({
        email: get(reqData, 'email'),
        identification_no: get(reqData, 'identificationNo'),
        registration_id: get(reqData, 'IDRegChild'),
      })
    );

    return {
      data: {
        requireConfirmationForSpouse: get(resp, 'data.success', false),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msHasDuplicateSpouseEmailOrNric',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msRetainChild = async (_, reqData) => {
  try {
    if (!reqData?.childID) return { data: {}, success: true };

    const body = {
      ...convertObjToSnakeCase(reqData, [
        'child_id:childID',
        'next_expected_dop:nextExpectedDOP',
      ]),
      bypass_exceeding: reqData?.bypassExceeding || false,
    };
    const resp = await childService().put(...CHILD_P3.retainChild(body));

    return {
      data: { addOrUpdateRetainChildren: resp?.status === 204 },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRetainChild',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [
        {
          message:
            error?.response?.data?.error || error?.message || DEFAULT_ERROR,
          errCode: error?.response?.data?.errCode || error?.code,
        },
      ],
    };
  }
};

export const msWithdrawChildAtPreEnrolment = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: { withdrawChildAtPreEnrolment: {} },
          success: true,
        });
      }, 1000);
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msWithdrawChildAtPreEnrolment',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      errors: [get(error, 'message', DEFAULT_ERROR)],
    };
  }
};

export const msSaveRegistrationConsentOfflineForm = async (_, reqData) => {
  try {
    const registrationID = reqData.registrationID || reqData.enquiryID;
    const uploadData = get(reqData, 'getRegistrationConsentUploadURL', {});
    const fileUrl = reqData.fileUrl || uploadData.Key;
    const fileNameValue = reqData.fileName || uploadData.Filename;

    if (!registrationID || !fileUrl || !fileNameValue) {
      return {
        data: undefined,
        success: false,
        error: UPLOAD_ERROR,
      };
    }

    const payload = {
      file_name: fileNameValue,
      file_url: fileUrl,
    };

    const resp = await onboardingService().post(
      REGISTRATIONS.saveRegistrationConsentOfflineForm(registrationID),
      payload
    );

    return {
      success: get(resp, 'data.success', true),
      data: {
        saveRegistrationConsentOfflineForm: get(resp, 'data.success', true),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveRegistrationConsentOfflineForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', UPLOAD_ERROR)
      ),
    };
  }
};

export const msGetRegistrationConsentUploadURL = async (_, reqData) => {
  try {
    const file = get(reqData, 'file');
    const res = await onboardingService().post(
      REGISTRATIONS.getRegistrationConsentUploadURL,
      {
        registration_id: Number(reqData?.registrationID),
        file_upload: {
          file_name: file?.fileName,
          file_path: file?.filePath,
          file_size: file?.fileSize,
          mime_type: file?.mimeType,
        },
      }
    );

    return {
      data: {
        getRegistrationConsentUploadURL: {
          Url: res.data?.url,
          Key: res.data?.key,
          Filename: res.data?.filename,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUploadConsentOfflineForm',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getEcdaRegistrationChildByIds = (
  ecdaRegistrationChildIds = []
) => {
  if (ecdaRegistrationChildIds.length === 0) return Promise.resolve({});
  return onboardingService()
    .post(
      ...REGISTRATIONS.getEcdaRegistrationChildByIds(ecdaRegistrationChildIds)
    )
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msFindAllRegistrationChild = async (_, filter = {}) => {
  try {
    const onboardingSvc = onboardingService();
    const result = await onboardingSvc.post(
      ...REGISTRATIONS.findAllRegistrationChild(filter)
    );

    const data = transformRegistrationChild(result?.data);
    return {
      success: true,
      data: { findAllRegistrationChild: data },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFindAllRegistrationChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message') || DEFAULT_ERROR,
    };
  }
};

export const getEcdaRegistrationParentByIds = async (ecdaRegParentIds = []) => {
  if (ecdaRegParentIds?.length === 0) return {};
  return onboardingService()
    .post(...REGISTRATIONS.getEcdaRegistrationParentByIds(ecdaRegParentIds))
    .then(dt => retrieveListOrObject(dt, false))
    .catch(error => {
      DatadogHandler.addError(error);
      DatadogHandler.sendLog(
        error,
        {
          functionName: 'getEcdaRegistrationParentByIds',
          fileName,
        },
        'error'
      );
      return {
        success: false,
        error: get(error, 'message') || DEFAULT_ERROR,
      };
    });
};

export const getCentreAddressByIds = async (ids = []) => {
  try {
    if (ids?.length === 0) return {};

    const userSvc = userService();
    const centreSvc = centreSerivce();
    const centreData = await centreSvc.post(...CENTRE.GET_CENTRE_BY_IDS(ids));
    const addressData = await userSvc.get(
      ...CENTRE_P3.getAddressesByCentreIds(ids)
    );
    const addresses = addressData?.data?.data || {};
    const centres =
      centreData?.data?.data?.map(centre => ({
        ...centre,
        ...(addresses?.[centre.id] && {
          addresses: addresses?.[centre.id],
        }),
      })) || [];

    return {
      success: true,
      data: centres,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getCentreAddressByIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message') || DEFAULT_ERROR,
    };
  }
};

export const getECDARegistrationParentByIds = async (ecdaRegParentIds = []) => {
  try {
    const resp = await getEcdaRegistrationParentByIds(ecdaRegParentIds);
    const fkECDARegParentIds = compact(
      uniq(Object.keys(resp || {})?.map(k => parseInt(k, 10)))
    );

    let ecdaRegChildParents = {};
    if (fkECDARegParentIds?.length > 0) {
      const onboardingSvc = onboardingService();
      const data = await onboardingSvc.get(
        ...REGISTRATIONS.getECDARegChildParentByECDARegParentIds(
          fkECDARegParentIds
        )
      );
      ecdaRegChildParents = data?.data?.data || {};
    }

    const ecdaRegParents = Object.entries(resp).reduce((acc, [key, value]) => {
      if (!value) return acc;
      acc[key] = {
        ...value,
        ...ecdaRegChildParents?.[key]?.data?.[0],
        ...(ecdaRegChildParents?.[key] && {
          ecdaRegistrationChildParents: ecdaRegChildParents?.[key],
        }),
      };
      return acc;
    }, {});

    return {
      success: true,
      data: ecdaRegParents,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getECDARegistrationParentByIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message') || DEFAULT_ERROR,
    };
  }
};

export const getECDARegistrationChildByIds = async (ecdaRegChildIds = []) => {
  try {
    const resp = await getEcdaRegistrationChildByIds(ecdaRegChildIds);

    const ecdaRegChild = Object.entries(resp).reduce((acc, [key, value]) => {
      if (!value) return acc;
      acc[key] = {
        ...value,
      };
      return acc;
    }, {});

    return {
      success: true,
      data: ecdaRegChild,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getECDARegistrationChildByIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msFetchECDARegistrations = async (_, reqData) => {
  try {
    const { pagination } = reqData;
    const reqBody = {
      ...(reqData?.IDCentres?.length > 0 && {
        centre_ids: reqData?.IDCentres,
      }),
      ...(reqData?.createdFrom && {
        created_from: reqData?.createdFrom,
      }),
      ...(reqData?.createdTo && {
        created_to: reqData?.createdTo,
      }),
      ...(reqData?.enrollmentDateFrom && {
        enrolment_date_from: reqData?.enrollmentDateFrom,
      }),
      ...(reqData?.enrollmentDateTo && {
        enrolment_date_to: reqData?.enrollmentDateTo,
      }),
      ...(reqData?.IDLevels?.length > 0 && {
        level_ids: reqData?.IDLevels,
      }),
      ...(reqData?.IDPrograms?.length > 0 && {
        program_ids: reqData?.IDPrograms,
      }),
      ...(reqData?.searchString && {
        search_string: reqData?.searchString,
      }),
      ...(reqData?.statuses?.length > 0 && {
        statuses: reqData?.statuses,
      }),
    };
    const params = {
      page: pagination?.page || DEFAULT_PAGE,
      perPage: pagination?.perPage || DEFAULT_ITEMS_PER_PAGE,
      ...(pagination?.sort?.length > 0 && {
        ...retrieveSortForMS(pagination?.sort, 'customSort'),
      }),
    };
    const centreSvc = centreSerivce();
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...REGISTRATIONS.getECDARegistrations(reqBody, params)
    );
    const ecdaRegs = resp?.data?.data ?? [];
    const fkEcdaRegChildIds = compact(
      uniq(ecdaRegs?.map(item => item?.fk_ecda_registration_child))
    );
    const fkEcdaRegParentIds = compact(
      uniq(ecdaRegs?.map(item => item?.fk_ecda_registration_parent))
    );
    const fkCentres = compact(uniq(ecdaRegs?.map(item => item?.fk_centre)));
    const fkLevels = compact(uniq(ecdaRegs?.map(item => item?.fk_level)));
    const fkPrograms = compact(uniq(ecdaRegs?.map(item => item?.fk_program)));

    const promiseMap = {};
    if (fkEcdaRegChildIds?.length > 0) {
      promiseMap.ecdaRegChild =
        getECDARegistrationChildByIds(fkEcdaRegChildIds);
    }
    if (fkEcdaRegParentIds?.length > 0) {
      promiseMap.ecdaRegParent =
        getECDARegistrationParentByIds(fkEcdaRegParentIds);
    }
    if (fkCentres?.length > 0) {
      promiseMap.centerData = getCentreAddressByIds(fkCentres);
    }
    if (fkLevels?.length > 0) {
      const schoolSvc = schoolService();
      promiseMap.levelData = schoolSvc.post(
        ...SCHOOL.GET_LEVEL_BY_IDS(fkLevels)
      );
    }
    if (fkPrograms?.length > 0) {
      promiseMap.programData = centreSvc.post(
        ...CENTRE.GET_PROGRAMS_BY_IDS(fkPrograms)
      );
    }

    const { ecdaRegChild, ecdaRegParent, centerData, levelData, programData } =
      await executePromiseMap(promiseMap);
    const ecdaRegParents = ecdaRegParent?.data || {};
    const ecdaRegChildLists = ecdaRegChild?.data || {};
    const centres = centerData?.data || [];
    const levels = levelData?.data?.data || [];
    const programs = programData?.data?.data || [];

    const data = transformECDARegistrations({
      registrations: ecdaRegs,
      ecdaRegistrationChild: ecdaRegChildLists,
      ecdaRegistrationParent: ecdaRegParents,
      programs,
      centres,
      levels,
    });

    return {
      data: {
        getECDARegistrations: {
          totalCount: resp?.data?.totalCount || 0,
          data,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchECDARegistrations',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const getECDARegParentAttributesByECDARegParentIds = async (
  ecdaRegParentIds = []
) => {
  if (ecdaRegParentIds?.length === 0) return {};
  return onboardingService()
    .post(
      ...ECDA_REGISTRATIONS.getECDARegParentAttributesByECDARegParentIds(
        ecdaRegParentIds
      )
    )
    .then(dt => get(dt, 'data.ecda_registration_parent_attributes', {}))
    .catch(() => ({}));
};
export const msGetECDARegUploadDocumentList = async (_, reqData) => {
  try {
    const onboardingSvc = onboardingService();
    const childSvc = childService();
    const resp = await childSvc
      .get(
        CHILD_P3.getECDARequiredDocuments(+get(reqData, 'IDECDARegistration'))
      )
      .then(r => get(r, 'data'))
      .catch(() => ({ totalCount: 0, data: [] }));

    const documentTypeConfigIds = compact(
      uniq(get(resp, 'data')?.map(item => item?.fk_document_type_config))
    );
    const ecdaRegChildIds = compact(
      uniq(get(resp, 'data')?.map(item => item?.fk_ecda_registration_child))
    );
    const ecdaRegParentIds = compact(
      uniq(get(resp, 'data')?.map(item => item?.fk_ecda_registration_parent))
    );

    const [
      ecdaRegChildren,
      ecdaRegParents,
      documentTypeConfigs,
      documentTags,
      ecdaRegParentAttributes,
    ] = await Promise.all([
      getEcdaRegistrationChildByIds(ecdaRegChildIds),
      onboardingSvc
        .post(...REGISTRATIONS.getEcdaRegistrationParentByIds(ecdaRegParentIds))
        .then(dt => retrieveListOrObject(dt, false))
        .catch(() => ({})),
      childSvc
        .get(...CHILD_P3.getDocumentTypeConfigs(documentTypeConfigIds))
        .then(dt => retrieveListOrObject(dt, false))
        .catch(() => ({})),
      getDocumentTagsByDocTypeIds(documentTypeConfigIds),
      getECDARegParentAttributesByECDARegParentIds(ecdaRegParentIds),
    ]);

    const documentIds = uniq(
      compact(
        reduce(
          Object.values(documentTags),
          (acc, item) => {
            acc.push(...(get(item, 'data') || [])?.map(dt => dt?.fk_document));
            return acc;
          },
          []
        )
      )
    );

    const documents = await getDocumentByIds(documentIds);
    const userIds = uniq(
      (Object.values(documents) || [])?.map(dt => get(dt, 'uploaded_by'))
    );
    const users = await getUserByIds(userIds);

    return transformGetECDARegUploadDocumentList(
      resp,
      documentTypeConfigs,
      ecdaRegChildren,
      ecdaRegParents,
      ecdaRegParentAttributes,
      documentTags,
      documents,
      users
    );
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDARegUploadDocumentList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetEcdaRegEnrolmentForm = async (_, { registrationID }) => {
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      ECDA_REGISTRATIONS.getECDARegEnrolmentForm(registrationID)
    );
    const data = transformEnrolmentForm(resp?.data || {});
    return {
      success: true,
      data: { getECDARegEnrolmentForm: data },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEcdaRegEnrolmentForm',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetRegistrationCancellationDetail = async (_, reqData = {}) => {
  try {
    const schoolSvc = schoolService();
    const reqBody = {
      child_id: reqData?.IDChild,
      ecda_reg_child_id: reqData?.IDEcdaRegChild,
    };

    const resp = await schoolSvc.post(
      ...SCHOOL_P3.getRegistrationCancellationDetail(reqBody)
    );

    const responseData = get(resp, 'data', {});

    return {
      success: true,
      data: {
        getRegistrationCancellationDetail: convertObjToCamelCase(responseData),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetRegistrationCancellationDetail',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetCancellationReasons = async (_, { schoolID }) => {
  try {
    const res = await schoolService().get(SCHOOL_P3.getCancellationReasonList, {
      schoolID,
    });

    return {
      success: true,
      data: {
        getCancellationReasonList: (res?.data || []).map(item =>
          convertObjToCamelCase(item, [], ['Id:id'])
        ),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCancellationReasons',
        fileName,
      },
      'error'
    );
    return {
      data: { getCancellationReasonList: [] },
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetLifeSgExceptionList = async (_, { schoolID }) => {
  try {
    const res = await schoolService().get(SCHOOL_P3.getLifeSgExceptionList, {
      schoolId: schoolID,
    });

    return {
      success: true,
      data: {
        lifeSgExceptionList: {
          data: (res?.data?.data || []).map(item =>
            convertObjToCamelCase(item)
          ),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetLifeSgExceptionList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msUpsertLifeSgException = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            upsertLifeSgException: true,
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpsertLifeSgException',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(ex, 'message') || DEFAULT_ERROR],
    };
  }
};

export const msGetLifeSgExceptionSelection = async (_, reqData) => {
  try {
    const schoolId = get(reqData, 'schoolID');
    const ecdaRegID = get(reqData, 'IDECDARegistration');

    const resp = await onboardingService().get(
      ...ECDA_REGISTRATIONS.getLifeSgExceptionSelection(schoolId, ecdaRegID)
    );
    return {
      data: {
        getLifeSgExceptionSelection: get(resp, 'data', { data: [] }),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetLifeSgExceptionSelection',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetLorAgencyOptions = async () => {
  try {
    const schoolSvc = schoolService();
    const resp = await schoolSvc.get(SCHOOL_P3.getLorAgencyOptions);
    return {
      data: {
        getLorAgencyOptions: resp?.data || [],
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetLorAgencyOptions',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msUpsertRegLorDetail = async (_, reqData) => {
  try {
    const { lorDTO } = reqData;
    const payload = convertPascalToSnake(lorDTO);

    // Format dates to include time
    if (payload.start_date) {
      payload.start_date = moment(payload.start_date).format(
        'YYYY-MM-DD 00:00:00'
      );
    }
    if (payload.end_date) {
      payload.end_date = moment(payload.end_date).format('YYYY-MM-DD 00:00:00');
    }

    if (payload.fk_ecda_registration) {
      payload.fk_ecda_registration = parseInt(payload.fk_ecda_registration, 10);
    }

    const resp = await onboardingService().put(
      ...REGISTRATIONS.upsertRegLorDetail(payload)
    );

    return {
      success: true,
      data: {
        upsertRegLorDetail: convertObjToCamelCase(
          get(resp, 'data', true),
          [],
          ['ID:id']
        ),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpsertRegLorDetail',
        fileName,
      },
      'error'
    );
    const fullErrMsg = get(ex, 'response.data.error', DEFAULT_ERROR);
    return {
      success: false,
      errors: [{ message: fullErrMsg }],
    };
  }
};

export const msClearRegLorDetail = async (_, reqData) => {
  try {
    const { regId } = reqData;
    await onboardingService().delete(
      REGISTRATIONS.clearRegLorDetail({ ecdaRegID: regId })
    );

    return {
      success: true,
      data: {
        clearRegLorDetail: true,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msClearRegLorDetail',
        fileName,
      },
      'error'
    );
    const fullErrMsg = get(ex, 'response.data.error', DEFAULT_ERROR);
    return {
      success: false,
      errors: [{ message: fullErrMsg }],
    };
  }
};

export const msGetRegLorDetail = async (_, reqData) => {
  try {
    if (!reqData?.regId) return { success: true, data: {} };

    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      ECDA_REGISTRATIONS.getRegLorDetail({ ecdaRegID: reqData?.regId })
    );

    const [referralByCode, agencyNameCode] = await Promise.all([
      resp?.data?.referral_by_code
        ? getCodeById(resp?.data?.referral_by_code)
        : {},
      resp?.data?.agency_name_code
        ? getCodeById(resp?.data?.agency_name_code)
        : {},
    ]);

    const details = resp?.data?.id
      ? {
          ...convertObjToCamelCase(resp?.data),
          codeByReferralByCode: referralByCode?.id && {
            ...convertObjToCamelCase(referralByCode),
          },
          codeByAgencyNameCode: agencyNameCode?.id && {
            ...convertObjToCamelCase(agencyNameCode),
          },
        }
      : null;

    return {
      data: { getRegLorDetail: details },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetRegLorDetail',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msSaveEcdaRegEnrolmentForm = async (_, body) => {
  try {
    const { registrationID, step, regForm = {} } = body;
    const form = {};
    if (regForm.child) {
      form.child = convertObjToSnakeCase(regForm.child, [
        'ecda_registration_child.fk_id_type:ecdaRegistrationChild.fkIDType',
      ]);
    }
    if (regForm.emergencyContact) {
      form.emergency_contact = regForm.emergencyContact;
    }
    if (regForm.healthInformation) {
      form.health_information = regForm.healthInformation;
    }
    if (regForm.mainApplicant) {
      form.main_applicant = {
        address: regForm.mainApplicant.address,
        ecdaRegistrationChildParent: convertObjToSnakeCase(
          regForm.mainApplicant.ecdaRegistrationChildParent
        ),
        ecdaRegistrationParent: convertObjToSnakeCase(
          regForm.mainApplicant.ecdaRegistrationParent,
          ['fk_id_type:fkIDType']
        ),
        ecdaRegistrationParentGuardian:
          regForm.mainApplicant.ecdaRegistrationParentGuardian,
      };
    }
    if (regForm.spouse) {
      form.spouse = {
        ecdaRegistrationChildParent: convertObjToSnakeCase(
          regForm.spouse.ecdaRegistrationChildParent
        ),
        ecdaRegistrationParent: convertObjToSnakeCase(
          regForm.spouse.ecdaRegistrationParent,
          ['fk_id_type:fkIDType']
        ),
        ecdaRegistrationParentGuardian:
          regForm.spouse.ecdaRegistrationParentGuardian,
      };
    }

    const resp = await onboardingService().post(
      ECDA_REGISTRATIONS.saveECDARegEnrolmentForm,
      {
        ecda_registration_id: registrationID,
        step,
        registration_form: form,
      }
    );
    const data = resp?.data || {};

    const tcAcknowledgeByUserId = get(
      data,
      'ecda_registration.tc_acknowledge_by'
    );
    const isAcknowledgedByParent =
      tcAcknowledgeByUserId &&
      [
        get(data, 'main_applicant.ecda_registration_parent.fk_user'),
        get(data, 'spouse.ecda_registration_parent.fk_user'),
      ].includes(tcAcknowledgeByUserId);

    return {
      data: {
        saveECDARegEnrolmentForm: {
          allowedSteps: data.allowed_steps,
          isParentConsented: data.is_parent_consented,
          marketingChannels: {
            IsEmail: data.marketing_channels?.is_email,
            IsSMS: data.marketing_channels?.is_phone,
            IsPhone: data.marketing_channels?.is_sms,
          },
          ecdaRegistration: {
            ...convertObjToCamelCase(data.ecda_registration, [
              'giroStatus:giro_status_string',
              'paymentStatus:payment_status_string',
              'documentStatus:document_status_string',
              'type:type_string',
              'tcDocumentURL:tc_document_url',
              'tcDocumentDownloadURL:tc_document_url_presigned_url',
            ]),
            userByTcAcknowledgeBy: {
              isParent: isAcknowledgedByParent,
              ecdaRegistrationParentsByFkUser: {
                totalCount: isAcknowledgedByParent ? 1 : 0,
              },
            },
          },
          child: convertObjToCamelCase(data.child, [
            'fkIDType:fk_id_type',
            'typeOfReferral:type_of_referral_string',
          ]),
          mainApplicant: convertObjToCamelCase(data.main_applicant, [
            'ecdaRegistrationChildParent.type:ecda_registration_child_parent.type_string',
            'ecdaRegistrationParent.fkIDType:ecda_registration_parent.fk_id_type',
            'ecdaRegistrationParent.workingStatus:ecda_registration_parent.working_status_string',
            'ecdaRegistrationParent.maritalStatus:ecda_registration_parent.marital_status_string',
          ]),
          spouse: convertObjToCamelCase(data.spouse, [
            'ecdaRegistrationChildParent.type:ecda_registration_child_parent.type_string',
            'ecdaRegistrationParent.fkIDType:ecda_registration_parent.fk_id_type',
            'ecdaRegistrationParent.workingStatus:ecda_registration_parent.working_status_string',
            'ecdaRegistrationParent.maritalStatus:ecda_registration_parent.marital_status_string',
          ]),
          emergencyContactInfo: data.emergency_contact_info,
          healthInfo: data.health_info,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSaveEcdaRegEnrolmentForm',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(ex, 'message') || DEFAULT_ERROR],
    };
  }
};

export const msGetECDARegistratgionDocumentUploadURL = async (_, reqData) => {
  try {
    const onboardingSvc = onboardingService();
    const { IDECDARegistration, file } = reqData;
    const payload = {
      ecda_registration_id: Number(IDECDARegistration),
      file_upload: {
        file_name: file?.fileName || '',
        file_path: file?.filePath || '',
        file_size: file?.fileSize || 0,
        mime_type: file?.mimeType || '',
      },
    };

    const resp = await onboardingSvc.post(
      ...ECDA_REGISTRATIONS.getECDARegistrationDocumentUploadUrl(payload)
    );

    return {
      success: true,
      data: {
        getECDARegistrationDocumentUploadUrl: {
          Url: resp?.data?.url,
          Key: resp?.data?.key,
          Filename: resp?.data?.filename,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDARegistratgionDocumentUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSaveECDAUploadedRegDocument = async (_, body) => {
  try {
    const { uploads = [], IDECDARegistration, skipApproval } = body || {};

    await childService().post(CHILD_P3.saveECDARegistrationGiroDocument, {
      document_uploads: uploads.map(type => ({
        document_type_id: type.documentTypeId,
        document_uploads: type.documentUploads.map(file => ({
          file_name: file.filename,
          file_path: file.filepath,
          file_size: file.filesize,
          mime_type: file.mimetype,
        })),
      })),
      ecda_registration_id: Number(IDECDARegistration),
      skip_approval: skipApproval,
    });

    return {
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSaveECDAUploadedRegDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(ex, 'message') || DEFAULT_ERROR],
    };
  }
};

export const msApproveECDARegDocument = async (_, reqData) => {
  try {
    const body = {
      document_type_id: +get(reqData, 'IDDocumentType'),
      ecda_registration_id: +get(reqData, 'IDECDARegistration'),
      remarks: get(reqData, 'remarks', ''),
    };
    const resp = await childService().post(
      ...CHILD_P3.approveECDARegDocument(body)
    );
    return {
      success: true,
      data: {
        approveECDARegDocument: get(resp, 'data.success', true),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msApproveECDARegDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error') || DEFAULT_ERROR,
          errorCode: get(ex, 'response.data.errorCode') || DEFAULT_ERROR,
        },
      ],
    };
  }
};

export const msSkipECDARegDocument = async (_, reqData) => {
  try {
    const body = {
      document_type_id: +get(reqData, 'IDDocumentType'),
      ecda_registration_id: +get(reqData, 'IDECDARegistration'),
      remarks: get(reqData, 'remarks', ''),
    };
    const resp = await childService().post(
      ...CHILD_P3.skipECDARegDocument(body)
    );
    return {
      success: true,
      data: {
        skipECDARegDocument: get(resp, 'data.success', true),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSkipECDARegDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error') || DEFAULT_ERROR,
          errorCode: get(ex, 'response.data.errorCode') || DEFAULT_ERROR,
        },
      ],
    };
  }
};

export const msRejectECDARegDocument = async (_, reqData) => {
  try {
    const body = {
      document_type_id: +get(reqData, 'IDDocumentType'),
      ecda_registration_id: +get(reqData, 'IDECDARegistration'),
      remarks: get(reqData, 'remarks', ''),
    };
    const resp = await childService().post(
      ...CHILD_P3.rejectECDARegDocument(body)
    );
    return {
      success: true,
      data: {
        rejectECDARegDocument: get(resp, 'data.success', true),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRejectECDARegDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error') || DEFAULT_ERROR,
          errorCode: get(ex, 'response.data.errorCode') || DEFAULT_ERROR,
        },
      ],
    };
  }
};

export const msGetECDARegistrationTncUploadURL = async (_, reqData) => {
  try {
    const file = get(reqData, 'file');
    const res = await onboardingService().post(
      REGISTRATIONS.getECDARegistrationTCUploadUrl,
      {
        ecda_registration_id: +reqData?.IDECDARegistration,
        file_upload: {
          file_name: file?.fileName,
          file_path: file?.filePath,
          file_size: file?.fileSize,
          mime_type: file?.mimeType,
        },
      }
    );

    return {
      data: {
        getECDARegistrationTCUploadUrl: {
          Url: res.data?.url,
          Key: res.data?.key,
          Filename: res.data?.filename,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDARegistrationTncUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msSubmitECDARegForm = async (_, reqData) => {
  try {
    const onboardingSvc = onboardingService();

    const payload = {
      ecda_registration_id: Number(reqData?.IDECDARegistration),
      additional_info: {
        tc_document_url: reqData?.info?.tcDocumentURL || '',
        marketing_channels: {
          is_email: !!reqData?.info?.marketingChannels?.IsEmail,
          is_sms: !!reqData?.info?.marketingChannels?.IsSMS,
          is_phone: !!reqData?.info?.marketingChannels?.IsPhone,
        },
      },
    };

    const resp = await onboardingSvc.post(
      ...MS_PHASE_3_ENDPOINTS.ECDA_REGISTRATIONS.submitECDARegistrationForm(
        payload
      )
    );

    return {
      success: true,
      data: {
        submitECDARegistrationForm: resp?.data?.success,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSubmitECDARegForm',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetECDAMonthlyFeeForEnquiry = async (_, { IDRegistration }) => {
  try {
    if (!IDRegistration) {
      return {
        data: {},
        success: true,
      };
    }

    const financeSvc = financeService();
    const resp = await financeSvc.get(
      ECDA_REGISTRATIONS.getRegistrationDraftFeeInvoice({
        registrationID: IDRegistration,
      })
    );
    if (!resp?.data?.id) {
      return {
        data: {},
        success: true,
      };
    }

    const invoiceItemResp = await msGetInvoiceItemByIds(null, {
      invoice_ids: [resp?.data?.id],
    });
    const invoiceItems = invoiceItemResp?.data?.invoiceItems || {};
    return {
      data: {
        getEcdaRegistrationDraftMonthlyFeeInvoice: {
          ...retrieveObjectMapper(resp?.data, [
            'total_amount',
            'outstanding_amount',
            'status:status_string',
            'grand_total',
          ]),
          invoiceItems,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDAMonthlyFeeForEnquiry',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msFetchEnquiryDataByIdECDAMVP = async (_, reqData) => {
  const registrationID = get(reqData, 'registrationID');
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      ECDA_REGISTRATIONS.ecdaRegistrationByID(registrationID)
    );
    const centreId = get(resp, 'data.fk_centre');
    const programId = get(resp, 'data.fk_program');
    const levelId = get(resp, 'data.fk_level');
    const staffId = get(resp, 'data.cancelled_by');
    const ecdaRegistrationChild = get(resp, 'data.fk_ecda_registration_child');
    const ecdaRegistrationParent = get(
      resp,
      'data.fk_ecda_registration_parent'
    );
    const leadId = get(resp, 'data.fk_lead');
    const acknowledgeBy = get(resp, 'data.tc_acknowledge_by');
    const [
      centreByIds,
      addresses,
      programByIds,
      levelByIds,
      ecdaChildRegistration,
      sibilingData,
      ecdaParentRegistration,
      ecdaChildParentByEcdaIds,
      userAcknowledgeData,
      childDetails,
    ] = await Promise.all([
      centreSerivce()
        .post(...CENTRE.GET_CENTRE_BY_IDS([centreId]))
        .then(retrieveListOrObject)
        .catch(() => []),
      userService()
        .get(USER.GET_ADDRESS_BY_CENTRE_ID(centreId))
        .then(r => r.data)
        .catch(() => ({})),
      centreSerivce()
        .post(...CENTRE.GET_PROGRAMS_BY_IDS([programId]))
        .then(retrieveListOrObject)
        .catch(() => []),
      schoolService()
        .post(...SCHOOL.GET_LEVEL_BY_IDS([levelId]))
        .then(retrieveListOrObject)
        .catch(() => []),
      onboardingService()
        .get(REGISTRATIONS.getEcdaRegistrationChildById(ecdaRegistrationChild))
        .then(r => r.data)
        .catch(() => ({})),
      onboardingService()
        .get(REGISTRATIONS.getSiblingsByChildId(ecdaRegistrationChild))
        .then(retrieveListOrObject)
        .catch(() => []),
      onboardingService()
        .post(
          ...REGISTRATIONS.getEcdaRegistrationParentByIds([
            ecdaRegistrationParent,
          ])
        )
        .then(retrieveListOrObject)
        .catch(() => []),
      onboardingService()
        .get(
          REGISTRATIONS.getEcdaRegistraionChildParentsByEcdaRegistrationParentIds(
            [ecdaRegistrationParent]
          )
        )
        .then(retrieveListOrObject)
        .catch(() => []),
      getUserByAcknowledgeById(acknowledgeBy),
      onboardingService()
        .get(REGISTRATIONS.getEcdaRegistrationChildById(ecdaRegistrationChild))
        .then(r => r.data)
        .catch(() => ({})),
    ]);
    const fkChild = get(childDetails, 'fk_child');
    const ackUserId = get(userAcknowledgeData, 'id');
    let ackUserData = {};
    if (ackUserId) {
      ackUserData = await onboardingService().get(
        REGISTRATIONS.ecdaRegistrationGetUserById(ackUserId)
      );
    }
    let childData = {};
    if (fkChild) {
      childData = await financeService()
        .post(
          ...FINANCE.getFeeForChild({
            enrolment_date: `${moment(get(resp, 'data.enrolment_date')).format(
              'YYYY-MM-DD'
            )} 00:00:00`,
            id_centre: get(resp, 'data.fk_centre'),
            id_child: fkChild,
            id_ecda_registration: registrationID,
            id_ecda_registration_child: ecdaRegistrationChild,
            id_level: get(resp, 'data.fk_level'),
            id_program: get(resp, 'data.fk_program'),
          })
        )
        .catch(() => ({}));
    }
    const ecdaChildParentByEcdaIdsData =
      ecdaChildParentByEcdaIds?.[ecdaRegistrationParent];
    let leadData = {};
    if (leadId) {
      leadData = await onboardingService()
        .get(CENTRE_WAITLIST.getLeadById(leadId))
        .catch(() => ({}));
    }
    let staffData = {};
    let userData = {};
    if (staffId) {
      staffData = await userService()
        .get(USER_P3.getStaffFromStaffLoader(staffId))
        .catch(() => ({}));
      if (get(staffData, 'data.fk_user')) {
        userData = await userService()
          .get(USER_P3.getUserById(get(resp, 'data.fk_user')))
          .catch(() => ({}));
      }
    }
    const ecdaParentRegistrationData =
      ecdaParentRegistration?.[ecdaRegistrationParent];
    const transformedData = transformMsFetchEnquiryDataByIdECDAMVP({
      enquiryData: resp,
      centreByIds,
      address: addresses,
      programByIds,
      levelByIds,
      ecdaParentRegistrationData,
      ecdaChildParentByEcdaIdsData,
      ecdaChildRegistration,
      sibilingsData: sibilingData,
      leadData,
      userAcknowledgeData,
      ackUserData,
      childData,
      userData,
    });
    return {
      data: transformedData,
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchEnquiryDataByIdECDAMVP',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msSaveECDARegGiroApplication = async (_, reqData = {}) => {
  try {
    const financeSvc = financeService();
    const giroApplication = get(reqData, 'giroApplication', {});
    const reqBody = {
      ecda_registration_child_id: get(reqData, 'IDEcdaRegChild'),
      school_id: get(reqData, 'IDSchool'),
      is_sibling_cda: get(reqData, 'isSiblingCda'),
      giro_application: convertPascalToSnake(giroApplication),
    };

    const resp = await financeSvc.post(
      ...FINANCE.saveEcdaRegGiroApplication(reqBody)
    );

    const responseData = get(resp, 'data', {});
    return {
      success: true,
      data: {
        saveEcdaRegGiroApplication: convertObjToCamelCase(responseData, [
          'ID:id',
        ]),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSaveECDARegGiroApplication',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetEcdaCdaAccountStatementUploadURL = async (_, reqData) => {
  try {
    if (!reqData?.file) return { success: true, data: {} };

    const body = {
      ...(reqData?.IDChild && {
        child_id: reqData?.IDChild,
      }),
      ...(reqData?.IDEnquiry && {
        enquiry_id: reqData?.IDEnquiry,
      }),
      ...(reqData?.IDBankAccount && {
        bank_account_id: reqData?.IDBankAccount,
      }),
      ...(reqData?.IDEcdaRegChild && {
        ecda_reg_child_id: reqData?.IDEcdaRegChild,
      }),
      ...(reqData?.IDDocumentTypeConfig && {
        id_document_type_config: reqData?.IDDocumentTypeConfig,
      }),
      file: reqData?.file && convertObjToSnakeCase(reqData?.file),
    };

    const resp = await childService().post(
      ...ECDA_REGISTRATIONS.getECDACdaAccountStatementUploadURL(body)
    );

    return {
      data: {
        getEcdaCdaAccountStatementUploadURL:
          resp?.data &&
          retrieveObjectMapper(resp?.data, [
            'Url:url',
            'Key:key',
            'Filename:file_name',
          ]),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEcdaCdaAccountStatementUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetECDAPaymentDetailsForEnquiry = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            getEcdaRegistrationInvoice: {},
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDAPaymentDetailsForEnquiry',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetAllChildDiscountsEcdaRegistration = async (_, reqBody) => {
  try {
    const registrationChildID = get(reqBody, 'registrationChildID');

    const response = await financeService().post(
      ...FINANCE.getAllEcdaRegistrationChildDiscounts({
        childID: registrationChildID,
      })
    );
    const discountItems = await financeService().post(
      ...FINANCE.getDiscountItemsByIds(
        get(response, 'data.data', []).map(item =>
          get(item, 'fk_discount_item')
        )
      )
    );

    const transformedResponse = transformDiscountData(
      response,
      get(discountItems, 'data.data', [])
    );

    return {
      data: {
        getAllEcdaRegistrationChildDiscounts: transformedResponse,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllChildDiscountsEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddDiscountToEcdaRegistration = async (_, reqBody) => {
  try {
    const registrationChildID = get(reqBody, 'registrationChildID');
    const registrationID = get(reqBody, 'registrationID');
    const discountItemGroupID = get(reqBody, 'discountItemGroupID');
    const from = moment(get(reqBody, 'from')).format('YYYY-MM-DD HH:mm:ss');
    const to = moment(get(reqBody, 'to')).format('YYYY-MM-DD HH:mm:ss');
    const amount = get(reqBody, 'amount');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.addEcdaRegistrationChildDiscount({
        registration_child_id: registrationChildID,
        registration_id: registrationID,
        discount_item_group_id: discountItemGroupID,
        from,
        to,
        amount,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        addDiscountToEcdaRegistrationChild: {
          ID: get(responseData, 'id'),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddDiscountToEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddDiscountToChild = async (_, reqBody) => {
  try {
    const childID = get(reqBody, 'childID');
    const discountItemGroupID = get(reqBody, 'discountItemGroupID');
    const from = moment(get(reqBody, 'from')).format('YYYY-MM-DD HH:mm:ss');
    const to = moment(get(reqBody, 'to')).format('YYYY-MM-DD HH:mm:ss');
    const amount = get(reqBody, 'amount');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.addChildDiscount({
        child_id: childID,
        discount_item_group_id: discountItemGroupID,
        from,
        to,
        amount,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        addDiscountToChild: {
          ID: get(responseData, 'id'),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddDiscountToChild',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msRemoveChildDiscount = async (_, reqBody) => {
  try {
    const childID = get(reqBody, 'childID');
    const discountArrangementId = get(reqBody, 'discountArrangementId');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.removeChildDiscount({
        child_id: childID,
        discount_arrangement_id: discountArrangementId,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        removeDiscountFromChild: get(responseData, 'success', true),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveChildDiscount',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msRemoveChildDiscountForEcdaRegistration = async (_, reqBody) => {
  try {
    const ecdaRegistrationChildId = get(reqBody, 'registrationChildID');
    const discountArrangementId = get(reqBody, 'discountArrangementId');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.removeEcdaRegistrationChildDiscount({
        child_id: ecdaRegistrationChildId,
        discount_arrangement_id: discountArrangementId,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        removeDiscountFromEcdaRegistrationChild: get(
          responseData,
          'success',
          true
        ),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveChildDiscountForEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const checkTransactionInstanceStatus = async (
  transactionInstanceId,
  responseData = {},
  functionName = 'checkTransactionInstanceStatus'
) => {
  if (transactionInstanceId) {
    try {
      const transactionResponse = await transactionService().get(
        TRANSACTION.getTransactionInstance(transactionInstanceId)
      );

      const transactionData = get(transactionResponse, 'data', {});
      const transactionInstance = get(
        transactionData,
        'transaction_instance',
        {}
      );
      const statusString = get(transactionInstance, 'status_string', '');

      return statusString === 'COMPLETED';
    } catch (transactionError) {
      DatadogHandler.addError(transactionError);
      DatadogHandler.sendLog(
        transactionError,
        {
          functionName: `${functionName}_transactionService`,
          fileName,
          transactionInstanceId,
        },
        'error'
      );
      return false;
    }
  } else {
    return get(responseData, 'success', true);
  }
};

export const msDeactivateChildDiscount = async (_, reqBody) => {
  try {
    const childID = get(reqBody, 'childID');
    const discountArrangementId = get(reqBody, 'discountArrangementId');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.deactivateChildDiscount({
        child_id: childID,
        discount_arrangement_id: discountArrangementId,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});
    const transactionInstanceId = get(responseData, 'transaction_instance_id');

    const deactivateChildDiscount = await checkTransactionInstanceStatus(
      transactionInstanceId,
      responseData,
      'msDeactivateChildDiscount'
    );

    return {
      data: {
        deactivateChildDiscount,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msDeactivateChildDiscount',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetEcdaRegistrationChildBalanceSheet = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            getEcdaRegistrationChildBalanceSheet: {},
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEcdaRegistrationChildBalanceSheet',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msInitiateRefundForCancelledEcdaRegistration = async (
  _,
  reqData
) => {
  try {
    const reqBody = {
      is_cda: reqData?.isCDA || false,
      is_non_cda: reqData?.isNonCDA || false,
      registration_id: reqData?.IDRegistration,
      remarks: reqData?.remarks,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...REFUND.initiateRefundForCancelledEcdaRegistration(reqBody)
    );
    const initiated = resp?.data?.initiated || false;
    return {
      success: true,
      data: {
        initiateRefundForCancelledEcdaRegistration: initiated,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msInitiateRefundForCancelledEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
          errCode: ex?.response?.data?.errCode || ex?.code,
        },
      ],
    };
  }
};

export const msSubmitNewWithdrawalRequest = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            submitNewWithdrawalRequest: {},
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSubmitNewWithdrawalRequest',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msHandleWithdrawalManualFormComplete = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            handleWithdrawalManualFormComplete: true,
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msHandleWithdrawalManualFormComplete',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetTempWithdrawalEffectiveToDate = async (_, reqData) => {
  try {
    const resp = await childService().post(
      CHILD_P3.getTempWithdrawalEffectiveToDate,
      {
        start_date: get(reqData, 'startDate'),
      }
    );
    return {
      data: {
        getTemporaryWithdrawalEndDate: get(resp, 'data.end_dates') || [],
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetTempWithdrawalEffectiveToDate',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetEstimatedWithdrawalEffectiveDateList = async (_, reqData) => {
  try {
    const resp = await childService().get(
      CHILD_P3.getEstimatedWithdrawalEffectiveDateList(get(reqData, 'childId'))
    );
    return {
      getEstimatedWithdrawalEffectiveDateList: {
        data: get(resp, 'data') || [],
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEstimatedWithdrawalEffectiveDateList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msFetchEgiroDetailsByECDARegChild = async (_, reqData) => {
  try {
    const ecdaRegistrationChildId = get(reqData, 'ecdaRegistrationChildId');

    const resp = await financeService().get(
      FINANCE.getEgiroDetailsByECDARegChild(ecdaRegistrationChildId)
    );

    const bankAccounts = get(resp, 'data.bank_accounts', []);

    const childIds = compact(
      uniq(bankAccounts.map(account => account.fk_child))
    );

    const bankBicCodeIds = compact(
      uniq(bankAccounts.map(account => account.fk_bank_bic_code))
    );

    const bankAccountIds = compact(
      uniq(bankAccounts.map(account => account.id))
    );

    const [children, bankBicCodes, bankAccountStateHistories, documentTypes] =
      await Promise.all([
        getChildByIds(childIds),
        getBankBicCodeByIds(bankBicCodeIds),
        getBankAccountStateHistoryByAccountIds(bankAccountIds),
        getDocumentTypesByBankAccountIds(bankAccountIds),
      ]);

    return transformFetchEgiroDetailsByECDARegChild(
      resp,
      children,
      bankBicCodes,
      bankAccountStateHistories,
      documentTypes
    );
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchEgiroDetailsByECDARegChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msFindAllECDARegistrationChild = async () => {
  try {
    const resp = await onboardingService()
      .post(ECDA_REGISTRATIONS.getAllECDARegistrationChild, {
        filter: {},
      })
      .catch(() => ({ data: { data: [], totalCount: 0 } }));

    return {
      findAllEcdaRegistrationChild: {
        data: get(resp, 'data.data', [])?.map(d =>
          retrieveObjectMapper(d, [
            'ID:id',
            'fullname',
            'fk_child',
            'birth_certificate',
          ])
        ),
        totalCount: get(resp, 'data.totalCount', 0),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFindAllECDARegistrationChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msCreateRegistrationECDAMVP = async (_, reqData) => {
  try {
    const body = {
      ...(reqData?.bypassExceeding && {
        bypass_exceeding: reqData?.bypassExceeding,
      }),
      registration_dto: {
        centre_id: get(reqData, 'newRegistration.centreId'),
        child_birth_certificate: get(
          reqData,
          'newRegistration.childBirthCertificate'
        ),
        child_date_of_birth: moment(
          get(reqData, 'newRegistration.childDateOfBirth')
        ).format('YYYY-MM-DD'),
        child_name: get(reqData, 'newRegistration.childName'),
        child_nationality: get(reqData, 'newRegistration.childNationality'),
        email: get(reqData, 'newRegistration.email'),
        enquiry_date: moment(
          get(reqData, 'newRegistration.enquiryDate')
        ).format('YYYY-MM-DD'),
        enrolment_date: moment(
          get(reqData, 'newRegistration.enrolmentDate')
        ).format('YYYY-MM-DD'),
        level_id: get(reqData, 'newRegistration.levelId'),
        name: get(reqData, 'newRegistration.name'),
        phone: get(reqData, 'newRegistration.phone'),
        phone_country_code: get(reqData, 'newRegistration.phoneCountryCode'),
        programme_id: get(reqData, 'newRegistration.programmeId'),
        relationship_id: get(reqData, 'newRegistration.relationshipID'),
        work_place_staff: get(reqData, 'newRegistration.workPlaceStaff'),
      },
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...ECDA_REGISTRATIONS.createNewEcdaRegistration(body)
    );
    if (get(resp, 'data.id')) {
      return {
        success: true,
        data: {
          createNewECDARegistration: {
            ID: get(resp, 'data.id'),
            fkEcdaRegistrationChild: get(
              resp,
              'data.fk_ecda_registration_child'
            ),
          },
        },
      };
    }
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateRegistrationECDAMVP',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error') || DEFAULT_ERROR,
          extensions: {
            ...get(ex, 'response.data.extensions', {}),
            errorCode: get(ex, 'response.data.errorCode') || DEFAULT_ERROR,
            error: get(ex, 'response.data.error') || DEFAULT_ERROR,
          },
        },
      ],
    };
  }
};

export const msRequireConfirmationForECDASpouse = async (_, reqData) => {
  try {
    const resp = await onboardingService().post(
      ...REGISTRATIONS.requireConfirmationForECDASpouse({
        email: get(reqData, 'email'),
        id_reg_child: get(reqData, 'IDECDARegChild'),
        identification_no: get(reqData, 'identificationNo'),
      })
    );
    return {
      success: true,
      data: {
        requireConfirmationForECDASpouse: get(resp, 'data.required', false),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRequireConfirmationForECDASpouse',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetECDAGiroFormUploadURL = async (_, reqData) => {
  try {
    const resp = await childService().post(CHILD_P3.getEcdaGiroFormUploadURL, {
      bank_account_id: get(reqData, 'IDBankAccount'),
      child_id: get(reqData, 'IDChild'),
      ecda_reg_child_id: get(reqData, 'IDEcdaRegChild'),
      id_document_type_config: get(reqData, 'IDDocumentTypeConfig'),
      enquiry_id: get(reqData, 'IDEnquiry'),
      file: convertObjToSnakeCase(get(reqData, 'file')),
    });

    return {
      data: {
        GetEcdaGiroFormUploadURL: {
          Filename: get(resp, 'data.file_name'),
          Url: get(resp, 'data.url'),
          Key: get(resp, 'data.key'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDAGiroFormUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msSkipGiroForECDA = async (_, reqData) => {
  try {
    const registrationId = get(reqData, 'IDEcdaRegistration');
    const response = await onboardingService().post(
      MS_PHASE_3_ENDPOINTS.CHILD.skipGiroForECDA(registrationId)
    );

    return {
      data: response.data,
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSkipGiroForECDA',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msCancelEcdaRegistration = async (_, reqData) => {
  try {
    const {
      registrationID,
      isReapplication,
      remarks,
      cancellationReason,
      cancellationSubreasonList,
      bypassExceeding,
    } = reqData;

    const body = {
      ecda_registration_id: registrationID,
      is_reapplication: isReapplication,
      id_cancellation_reason: Number(cancellationReason),
      bypass_exceeding: bypassExceeding,
      ...(remarks && { remarks }),
      ...(cancellationSubreasonList &&
        cancellationSubreasonList.length > 0 && {
          cancellation_subreason_list: cancellationSubreasonList.map(
            subreason => ({
              parent_label: subreason.parentLabel,
              remarks: subreason?.remarks || '',
              subreason_fk_code: Number(subreason.subreasonFkCode),
            })
          ),
        }),
    };

    const res = await onboardingService().post(
      ...ECDA_REGISTRATIONS.cancelEcdaRegistration(body)
    );

    return {
      data: {
        cancelECDARegistration: {
          ID: get(res, 'data.id', registrationID),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelEcdaRegistration',
        fileName,
      },
      'error'
    );

    return {
      data: undefined,
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'response.data.message') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msReinstateEcdaRegistration = async (_, reqData) => {
  try {
    const { registrationID, remarks } = reqData;
    const body = {
      ecda_registration_id: registrationID,
      ...(remarks && { remarks }),
    };

    const res = await onboardingService().put(
      ECDA_REGISTRATIONS.reinstateEcdaRegistration,
      body
    );

    return {
      success: res?.data?.success,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msReinstateEcdaRegistration',
        fileName,
      },
      'error'
    );

    return {
      success: false,
      errors: [get(error, 'message') || DEFAULT_ERROR],
    };
  }
};

export const msGetECDAExistingParent = async (_, { email }) => {
  try {
    if (email?.trim()?.length === 0) {
      return {
        success: true,
        data: {
          getECDAExistingParent: {},
        },
      };
    }
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...REGISTRATIONS.getECDAExistingParent({ email })
    );
    const existingParent = resp?.data || {};
    const parentId = existingParent?.parent?.id;
    let parentRelation = '';
    if (parentId) {
      const childSvc = childService();
      const response = await childSvc.get(
        CHILD_P3.getParentRelationById(parentId)
      );
      parentRelation = response?.data?.relation || '';
    }
    const data = transformECDAExistingParent(existingParent, parentRelation);
    return {
      success: true,
      data: {
        getECDAExistingParent: data,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDAExistingParent',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(ex, 'message') || DEFAULT_ERROR],
    };
  }
};

/* eslint-disable camelcase */
export const msFetchECDAExistingChild = async (
  _,
  { birthCertificate, dateOfBirth }
) => {
  try {
    const childSvc = childService();
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...REGISTRATIONS.getECDAExistingChild({
        birth_certificate: birthCertificate,
        date_of_birth: dateOfBirth,
      })
    );

    let data = null;
    if (resp?.data) {
      let childRelations = [];
      const childId = resp?.data?.child?.id;
      if (childId) {
        const cRelations = await childSvc.get(
          CHILD_P3.getApplicantsOfChild(childId)
        );
        childRelations = cRelations?.data?.data?.filter(
          ({ type_string }) => type_string === APPLICANT
        );
      }
      let ecdaRegistrationChildParent = [];
      const fkECDARegChildId = resp?.data?.ecda_reg_child?.id;
      if (fkECDARegChildId) {
        const childParents = await onboardingSvc.get(
          REGISTRATIONS.getECDARegistrationChildParentsById(fkECDARegChildId)
        );
        ecdaRegistrationChildParent = childParents?.data?.data?.filter(
          ({ type_string }) => type_string === APPLICANT
        );
      }
      data = transformECDAExistingChild({
        data: resp?.data,
        childParents: ecdaRegistrationChildParent,
        childRelations,
      });
    }
    return {
      success: true,
      data: {
        getECDAExistingChild: data,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchECDAExistingChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(ex, 'message') || DEFAULT_ERROR],
    };
  }
};

export const msGetApplicantChanges = async (_, { IDRegChild }) => {
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      REGISTRATIONS.getRegParentChangesByRegChildId(IDRegChild)
    );
    const mainApplicant = resp?.data?.main_applicant || [];
    const spouse = resp?.data?.spouse || [];
    return {
      success: true,
      data: {
        getRegParentChanges: {
          mainApplicant: mainApplicant?.map(item => ({
            ...retrieveObjectMapper(item, ['label', 'new_value', 'old_value']),
          })),
          spouse: spouse?.map(item => ({
            ...retrieveObjectMapper(item, ['label', 'new_value', 'old_value']),
          })),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetApplicantChanges',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [get(ex, 'message') || DEFAULT_ERROR],
    };
  }
};

export const msEditRegistrationECDAMVP = async (_, reqBody) => {
  try {
    const body = {
      id_registration: get(reqBody, 'IDRegistration'),
      remarks: get(reqBody, 'remarks'),
      registration_dto: {
        centre_id: get(reqBody, 'editRegistration.centreId'),
        child_birth_certificate: get(
          reqBody,
          'editRegistration.childBirthCertificate'
        ),
        ...(get(reqBody, 'editRegistration.childDateOfBirth') && {
          child_date_of_birth: moment(
            get(reqBody, 'editRegistration.childDateOfBirth')
          ).format(DATE_PICKER_FORMAT),
        }),
        ...(get(reqBody, 'editRegistration.enquiryDate') && {
          enquiry_date: moment(
            get(reqBody, 'editRegistration.enquiryDate')
          ).format(DATE_PICKER_FORMAT),
        }),
        ...(get(reqBody, 'editRegistration.enrolmentDate') && {
          enrolment_date: moment(
            get(reqBody, 'editRegistration.enrolmentDate')
          ).format(DATE_PICKER_FORMAT),
        }),
        child_name: get(reqBody, 'editRegistration.childName'),
        child_nationality: get(reqBody, 'editRegistration.childNationality'),
        email: get(reqBody, 'editRegistration.email'),
        level_id: get(reqBody, 'editRegistration.levelId'),
        name: get(reqBody, 'editRegistration.name'),
        phone: get(reqBody, 'editRegistration.phone'),
        phone_country_code: get(reqBody, 'editRegistration.phoneCountryCode'),
        programme_id: get(reqBody, 'editRegistration.programmeId'),
        relationship_id: get(reqBody, 'editRegistration.relationshipID'),
        work_place_staff: get(reqBody, 'editRegistration.workPlaceStaff'),
      },
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.put(
      ...ECDA_REGISTRATIONS.editRegistrationECDAMVP(body)
    );
    return {
      success: true,
      data: {
        editECDARegistration: retrieveObjectMapper(resp?.data, [
          'ID:id',
          'fk_ecda_registration_child',
        ]),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msEditRegistrationECDAMVP',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error') || DEFAULT_ERROR,
          extensions: {
            ...get(ex, 'response.data.extensions', {}),
            errorCode: get(ex, 'response.data.errorCode') || DEFAULT_ERROR,
            error: get(ex, 'response.data.error') || DEFAULT_ERROR,
          },
        },
      ],
    };
  }
};

export const msMergeEcdaRegistrationParentEmail = async (_, reqData) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    const { ecdaRegistrationParentID, newEmail, oldEmail } = reqData;

    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        mergeEcdaRegistrationParentEmail: {
          success: true,
          ecdaRegistrationParentId: ecdaRegistrationParentID,
          oldEmail,
          newEmail,
          message: `Successfully merged ECDA registration parent email (Mock MS Response) - Old: ${oldEmail}, New: ${newEmail}`,
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 145));

    return mockResponse;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msMergeEcdaRegistrationParentEmail',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msRemoveECDARegUploadedDocument = async (_, reqData) => {
  try {
    const reqBody = {
      ...(reqData?.IDDocument && {
        document_type_id: reqData?.IDDocument,
      }),
      ...(reqData?.IDECDARegistration && {
        ecda_registration_id: +reqData?.IDECDARegistration,
      }),
      ...(reqData?.remarks && {
        remarks: reqData?.remarks,
      }),
    };
    const childSvc = childService();
    const resp = await childSvc.post(
      ...ECDA_REGISTRATIONS.removeECDARegistrationDocument(reqBody)
    );
    return {
      success: true,
      data: {
        removeECDARegDocument: resp?.data?.success || false,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRemoveECDARegUploadedDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msRevertECDARegDocument = async (_, reqData) => {
  try {
    const body = {
      document_type_id: +get(reqData, 'IDDocumentType'),
      ecda_registration_id: +get(reqData, 'IDECDARegistration'),
      remarks: get(reqData, 'remarks', ''),
    };
    const resp = await childService().post(
      ...CHILD_P3.revertECDARegDocument(body)
    );
    return {
      success: true,
      data: {
        revertECDARegDocument: get(resp, 'data.success', true),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRevertECDARegDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
    };
  }
};
export const getRegParentByUserIds = async (userIds = []) => {
  if (!userIds?.length) return [];
  return onboardingService()
    .post(...REGISTRATIONS.getRegParentByUserIds(userIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msGetRegAdditionalInfo = async (_, reqData) => {
  const { regID } = reqData;
  try {
    const resp = await onboardingService().get(
      REGISTRATIONS.getRegAdditionalInfo(regID)
    );
    const data = resp?.data || {};

    return {
      data: {
        getRegAdditionalInfo: {
          HouseholdInfo: {
            PrimaryApplicantHighestEducation:
              data.household_info?.primary_applicant_highest_education,
            SecondaryApplicantHighestEducation:
              data.household_info?.secondary_applicant_highest_education,
            FamilyMemberCount: data.household_info?.family_member_count,
          },
          EmergencyContactInfo: convertObjToCamelCase(
            data.emergency_contact_info,
            ['mobilePhoneCC:mobile_phone_cc']
          ),
          HealthInfo: data.health_info,
          MarketingChannels: {
            IsEmail: data.marketing_channels?.is_email,
            IsSMS: data.marketing_channels?.is_sms,
            IsPhone: data.marketing_channels?.is_phone,
          },
          Registration: convertObjToCamelCase(data.registration),
          Step: data.step,
          IsConsented: data.is_consented,
          MarketingConsent: data.marketing_consent,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetRegAdditionalInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getRegChildParentsByParentIds = async (
  parentIds = [],
  params = {}
) => {
  if (!parentIds?.length) return [];
  return onboardingService()
    .post(...REGISTRATIONS.getRegChildParentsByParentIds(parentIds, params))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getReqParentsByIds = async (regParentIds = []) => {
  if (!regParentIds?.length) return [];
  return onboardingService()
    .post(...REGISTRATIONS.getReqParentsByIds(regParentIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msGetMarketingConsentByParent = async (_, reqData) => {
  const { parentID } = reqData;
  try {
    const resp = await onboardingService().get(
      `${REGISTRATIONS.getMarketingConsentBusinessEntityByParent}/${parentID}`
    );
    return {
      data: {
        getMarketingConsentBusinessEntityByParentAndSchool:
          convertObjToCamelCase(get(resp, 'data')),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetMarketingConsentByParent',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msGetMarketingConsentDocumentUploadURL = async (_, reqData) => {
  try {
    const file = get(reqData, 'file');
    const resp = await onboardingService().post(
      REGISTRATIONS.getMarketingConsentDocumentUploadURL,
      {
        file_name: get(file, 'fileName'),
        file_path: get(file, 'filePath'),
        file_size: get(file, 'fileSize', 0),
        mime_type: get(file, 'mimeType'),
      }
    );
    return {
      success: true,
      data: {
        getMarketingConsentDocumentUploadURL: {
          Key: get(resp, 'data.key'),
          Url: get(resp, 'data.url'),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetMarketingConsentDocumentUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msUpdateMarketingConsentDashboard = async (_, reqData) => {
  try {
    const resp = await onboardingService().put(
      REGISTRATIONS.updateMarketingConsentDashboard,
      {
        marketing_consent_id: get(reqData, 'input.IdMarketingConsent'),
        document_url: get(reqData, 'input.documentUrl'),
        channels: (get(reqData, 'input.Channels') || []).map(i => ({
          id: i.Id,
          name: i.Name || '',
          type: i.Type || '',
          is_email: i.IsEmail || false,
          is_phone: i.IsPhone || false,
          is_sms: i.IsSMS || false,
          description: i.Description || '',
        })),
      }
    );
    return {
      success: get(resp, 'data.success', false),
      data: {
        updateMarketingConsentDashboard: get(resp, 'data.success'),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateMarketingConsentDashboard',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msGetEcdaSiblingBirthCertificateUploadURL = async (_, reqData) => {
  try {
    if (!reqData?.file || !reqData?.IDBankAccount) {
      return {
        data: {
          getEcdaSiblingBirthCertificateUploadURL: null,
        },
        success: true,
      };
    }

    const body = {
      ...(reqData?.IDChild && {
        child_id: reqData?.IDChild,
      }),
      ...(reqData?.IDEnquiry && {
        enquiry_id: reqData?.IDEnquiry,
      }),
      ...(reqData?.IDEcdaRegChild && {
        ecda_reg_child_id: reqData?.IDEcdaRegChild,
      }),
      ...(reqData?.IDDocumentTypeConfig && {
        id_document_type_config: reqData?.IDDocumentTypeConfig,
      }),
      bank_account_id: reqData?.IDBankAccount,
      file: convertObjToSnakeCase(reqData?.file),
    };
    const resp = await childService().post(
      ...ECDA_REGISTRATIONS.getEcdaSiblingBirthCertificateUploadURL(body)
    );

    return {
      data: {
        getEcdaSiblingBirthCertificateUploadURL:
          resp?.data &&
          retrieveObjectMapper(resp?.data, [
            'Filename:file_name',
            'Url:url',
            'Key:key',
          ]),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEcdaSiblingBirthCertificateUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const getECDARegistrationsByRegChildIds = async (regChildIds = []) => {
  if (!regChildIds?.length) return {};
  return onboardingService()
    .get(...ECDA_REGISTRATIONS.getByRegChildIds(regChildIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msStartEcdaRegistration = async (_, reqData) => {
  try {
    const { registrationID } = reqData;
    const res = await onboardingService().put(
      ECDA_REGISTRATIONS.startEcdaRegistration(registrationID)
    );
    return {
      data: {
        startECDARegistration: res?.data?.success || false,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msStartEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};
